import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { RefreshCw, ChevronLeft, ChevronRight, ChevronDown, Info, Lightbulb, Lock, Unlock, Check, Trash2, AlertTriangle, Download, Send } from 'lucide-react';
import { useRoosterStore } from '../store/useRoosterStore.js';
import ConflictBanner from '../components/ConflictBanner.jsx';
import { weekBereikKort, weekNaarMaandag, bouwAfwezigheidMap, bouwAfwezigheidTypeMap, ALLE_DAGEN, DAG_LANG } from '../data/afwezigheid.js';
import { maandWeken, isoWeekNummer } from '../data/maand.js';
import { vakantieDagenInWeek } from '../data/vakanties.js';
import { schoolweekType, isBeschikbaar } from '../engine/rooster-engine.js';
import { analyseerWeek, bepaalReeksSignalen } from '../engine/analyseer-week.js';
import {
  CEL, getCel, isWerkCode, legendaCodesVoor, dienstGroepenPerCode, LEGENDA_RESERVE, afwezigStijl,
  overrideCode, overrideUren,
  KANTOOR_UREN_STANDAARD, KANTOOR_UREN_MIN, KANTOOR_UREN_MAX, KANTOOR_UREN_STAP,
} from '../lib/roostercodes.js';
import { exporteerMaandroosterExcel } from '../lib/exportMaandroosterExcel.js';
import { isRoosterbaar } from '../lib/rollen.js';
import { stuurPublicatieMelding } from '../lib/api.js';
import { echteAfwijkingen } from '../lib/divergentie.js';
import { versBalansVoorBlok, zoekWeekObject } from '../lib/balans.js';
import { ruilOpCel } from '../lib/ruilmarkering.js';
import { handmatigOpCel } from '../lib/handmatigmarkering.js';
import { handmatigTitel } from '../lib/handmatigLabels.js';

const DAGEN = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const DAG_KORT  = { ma: 'Ma', di: 'Di', wo: 'Wo', do: 'Do', vr: 'Vr', za: 'Za', zo: 'Zo' };
const MAANDEN = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

// --- Kalenderdatum van een (week, dag) voor de reeks-melding ---------------------
// Intl met een EXPLICIETE tijdzone, nooit toISOString: die rekent naar UTC en levert in de
// Nederlandse zomertijd de vorige dag (zie lib/tijd.js voor de vier keer dat dat misging).
const REEKS_DATUM_FORMAT = new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'Europe/Amsterdam', day: 'numeric', month: 'long',
});

// weekNaarMaandag geeft een LOKAAL middernacht-object. Staat de browser in een andere tijdzone
// dan Europe/Amsterdam, dan kan omrekenen naar Amsterdam de kalenderdag omklappen. Het anker op
// 12:00 zetten maakt dat onmogelijk: een halve dag speling is meer dan elk tijdzoneverschil.
function reeksDatum(weeknummer, dag, isoJaar) {
  const d = weekNaarMaandag(weeknummer, isoJaar);
  d.setDate(d.getDate() + Math.max(0, DAGEN.indexOf(dag)));
  d.setHours(12, 0, 0, 0);
  return REEKS_DATUM_FORMAT.format(d);
}

// Kalenderdatum voor een melding, of null als hij niet betrouwbaar te bepalen is.
// Dezelfde helper als de reeks-melding gebruikt, met een poort ervoor — want reeksDatum
// valt bij een onbekende dag-code stil terug op maandag (Math.max(0, indexOf) hierboven)
// en een verkeerde datum is erger dan geen: de melding wijst de teamleider dan naar de
// verkeerde dag. null betekent hier "toon de oude tekst zonder datum".
function meldingDatum(weeknummer, dag, isoJaar) {
  if (!Number.isFinite(weeknummer) || !Number.isFinite(isoJaar)) return null;
  if (!DAGEN.includes(dag)) return null;
  return reeksDatum(weeknummer, dag, isoJaar);
}

// Wat een dag ZONDER handmatige markering waard is in de verschilmeting hieronder: één
// dienst = 8 uur. Zelfde grondslag als balans.js:36 (UUR_PER_DIENST) en analyseer-week.js:85.
// Bewust een eigen constante en niet KANTOOR_UREN_STANDAARD: die gaat over wat een
// KANTOORmarkering waard is, dit over wat er stond voordat er gemarkeerd werd.
const UREN_ZONDER_MARKERING = 8;

// Uren compact voor een smalle matrixcel: 3 → "3", 0.5 → "0,5", 11.5 → "11,5".
// Nederlandse komma, geen eenheid — die past niet naast de code in 28px (zie de badge in
// RoosterMatrix); de volledige tekst staat in de tooltip.
const urenKort = (n) => String(n).replace('.', ',');

// Uren voor de BALANS: afgerond op één decimaal, Nederlandse komma, en een heel getal
// zonder decimaal ("8u", niet "8,0u"). Inclusief de eenheid, want elke aanroeper plakt
// die er toch achter.
//
// ⚠️ ALLEEN WEERGAVE. De onderliggende waarde blijft ongemoeid en mag NOOIT afgerond
// worden opgeslagen: een handmatig verloftotaal wordt over de werkdagen van de hele
// periode verdeeld waarbij de laatste dag het restant krijgt (data/verlofuren.js), zodat
// de som exact het ingevoerde getal is. 40u over 7 dagen geeft 5,714285714285714 per dag;
// rond je dát af, dan telt het niet meer op tot 40. Loopt zo'n periode bovendien over een
// blokgrens, dan is de balans van één blok gewoon een gebroken getal — daar is niets mis
// mee, het hoort alleen niet met veertien decimalen op het scherm.
//
// Bewust NIET urenKort hierboven: die rondt niet af en draagt geen eenheid. Hij dient de
// 28px-matrixcel, waar een kantoorurental sowieso al op halve uren staat.
// De waarde zoals urenNet hem TOONT, maar als getal. Apart, omdat kleur en vinkje
// dezelfde waarde moeten volgen als het getal op het scherm — anders staat er "+0u"
// in het rood zonder vinkje bij een balans van 0,04u, en dat leest als een fout.
const urenAfgerond = (n) => Math.round(n * 10) / 10;
const urenNet = (n) => `${String(urenAfgerond(n)).replace('.', ',')}u`;

// --- Markering-uren: UI-correctie op Gepland/Balans o.b.v. celOverrides ---
// Telt per medewerker het netto uren-effect van handmatige S/K/leeg-markeringen
// in een blok, door per overschreven cel getCel MET override te vergelijken met
// getCel ZONDER override. School-week-logica (week A/B) en afwezigheids-voorrang
// zitten al in getCel, dus de delta is automatisch correct. Raakt engine/golden niet.
// `vakanties` is de LIJST met periodes, niet één Set: deze functie loopt over meerdere weken
// en bouwt de vakantiedagen-Set per week zelf (zelfde bouw als weekAnalyses hieronder).
function berekenMarkeringDelta(blokWeken, medewerkers, celOverrides, afwezigheid, jaar, regels, vakanties = []) {
  // isWerkCode is de gedeelde blacklist uit roostercodes: alleen V/Z/A tellen niet
  // als werk. Hiervoor stond hier een whitelist ['D','L','R','LW','S','K'] — een
  // nieuwe code viel daar stil buiten en gaf 8 uur verschil in Gepland/Balans.
  const isWerk = (cel) => isWerkCode(cel?.code);

  // naam -> medewerker-object (overrides zijn op naam gesleuteld, niet op key k)
  const mwByNaam = {};
  for (const k of Object.keys(medewerkers || {})) {
    if (k.startsWith('_')) continue;
    mwByNaam[medewerkers[k].naam] = medewerkers[k];
  }

  const delta = {}; // naam -> uren (+/-)
  for (const week of blokWeken || []) {
    const wn = week.weeknummer;
    const overridesWeek = celOverrides?.[wn];
    if (!overridesWeek) continue;
    const weekType = schoolweekType(wn);
    const afwTW = bouwAfwezigheidTypeMap(afwezigheid, wn, week.isoJaar ?? jaar);
    // Vakantiedagen van DEZE week: in een schoolvakantie vervalt de schooldag, dus geeft
    // getCel dan geen S meer. Zonder deze Set telt de delta een verdwenen S nog als werk.
    const vakDagen = new Set(vakantieDagenInWeek(wn, week.isoJaar ?? jaar, vakanties));
    for (const naam of Object.keys(overridesWeek)) {
      const mw = mwByNaam[naam];
      if (!mw) continue;
      const afwPersoon = afwTW?.[naam];
      const dagen = overridesWeek[naam] || {};
      for (const dag of Object.keys(dagen)) {
        const ov = dagen[dag];
        const met    = getCel(naam, dag, week.rooster, mw, weekType, wn, afwPersoon, ov, regels, vakDagen);
        const zonder = getCel(naam, dag, week.rooster, mw, weekType, wn, afwPersoon, undefined, regels, vakDagen);
        // VERSCHILMETING, nu in UREN in plaats van in hele diensten.
        //
        // De kant MET de markering telt zijn EIGEN urenaantal: een kantoormarkering kan 3 uur
        // zijn. De kant ZONDER telt altijd 8 — dat is wat er stond voordat er gemarkeerd werd
        // (een dienst, een config-kantoordag, of een schooldag) en dat is in deze balans
        // onveranderd 8 waard. School blijft daarmee volledig buiten schot: overrideUren geeft
        // voor 'S' de standaard terug, dus een S-markering is nog steeds precies ±8.
        //
        // BESTAANDE DATA VERANDERT NIET. Een markering zonder urenveld geeft 8 aan de
        // met-kant, dus alle negen markeringen in de live-data houden hun huidige delta:
        //   K op een lege dag           8 − 0 = +8   (Jurgen wk34 ma)
        //   K op een dienst             8 − 8 =  0   (Jurgen wk33 di)
        //   K op een config-kantoordag  8 − 8 =  0   (Emre wk31 di)
        //   'leeg' op een dienst        0 − 8 = −8   (Jurgen wk32 zo)
        // Nieuw is alleen wat een EIGEN urenaantal doet: een K van 3 uur wordt +3 op een lege
        // dag en −5 bovenop een dienst — de kantooruren VERVANGEN die dienst, dus het verschil
        // hoort negatief te zijn.
        const urenMet = isWerk(met) ? overrideUren(ov) : 0;
        const urenZonder = isWerk(zonder) ? UREN_ZONDER_MARKERING : 0;
        const d = urenMet - urenZonder;
        if (d !== 0) delta[naam] = (delta[naam] || 0) + d;
      }
    }
  }
  return delta;
}

// ── Cel bewerken (handmatig wijzigen) ────────────────────────────────────────

// Waar staat een persoon op een dag? Hoogstens 1 (gemKey, dienstId) of null.
function vindPlaatsing(rooster, naam, dag) {
  if (!rooster) return null;
  for (const gemKey of Object.keys(rooster))
    for (const dienstId of Object.keys(rooster[gemKey]))
      if (rooster[gemKey][dienstId][dag]?.includes(naam)) return { gemKey, dienstId };
  return null;
}

// Effectieve dag-set van een dienst voor een weektype (zelfde keuze als de engine).
function dienstDagenVoorType(dienst, weekType) {
  if (weekType === 'A' && dienst.week_a_dagen) return dienst.week_a_dagen;
  if (weekType === 'B' && dienst.week_b_dagen) return dienst.week_b_dagen;
  if (dienst.week_a_dagen || dienst.week_b_dagen) return [];
  return dienst.dagen || [];
}

// `toonSoort=false` levert de kale gemeentenaam — zie soortNodig hieronder.
function dienstLabel(gemeenteNaam, dienstId, toonSoort = true) {
  if (!toonSoort) return gemeenteNaam;
  if (dienstId === 'dagdienst') return `${gemeenteNaam} — dag`;
  if (dienstId.startsWith('late_dienst') || dienstId === 'vrijdag' || dienstId === 'flexibel') return `${gemeenteNaam} — laat`;
  return gemeenteNaam;
}

/**
 * Moet het menu voor deze gemeente de DIENSTSOORT tonen, of volstaat de naam?
 *
 * Regel: de soort is alleen nodig als twee diensten van dezelfde gemeente op DEZELFDE DAG
 * kunnen vallen — dán moet de gebruiker echt kiezen. Vallen ze nooit samen, dan volgt de
 * dienst uit de dag en voegt "— dag"/"— laat" niets toe. Met de huidige data betekent dat:
 * Arnhem gesplitst (dagdienst en late_dienst_dw delen ma-vr), Lingewaard en Rheden samen.
 *
 * Bewust GEEN gemeentenaam in deze test: Arnhem is niet bijzonder omdát het Arnhem is, maar
 * omdat het als enige twee diensten op dezelfde dagen heeft. Zo werkt de regel vanzelf voor
 * een nieuwe omgeving zonder dat hier iets bij moet.
 *
 * Over A ∪ B, niet per weektype: zo is de menuvorm een eigenschap van de gemeente en wisselt
 * hij niet per week. Voor de huidige data geeft dat dezelfde uitkomst als per weektype rekenen.
 */
function soortNodig(gemeente) {
  const dagSets = Object.values(gemeente.diensten || {}).map(
    d => new Set([...dienstDagenVoorType(d, 'A'), ...dienstDagenVoorType(d, 'B')])
  );
  for (let i = 0; i < dagSets.length; i++)
    for (let j = i + 1; j < dagSets.length; j++)
      if ([...dagSets[i]].some(dag => dagSets[j].has(dag))) return true;
  return false;
}

// Alle (gemKey, dienstId) die op deze dag + weektype mogelijk zijn (diagnose punt 5).
function geldigeOptiesVoor(dag, weekType, regels) {
  // Handmatig menu = vrij: elke dienst is op elke dag plaatsbaar (override).
  // De regels sturen alleen de automatische generatie; dit menu raakt die niet.
  //
  // Per GEMEENTE groeperen, want de vraag "moet de dienstsoort erbij" is een gemeente-vraag
  // (zie soortNodig). Daarna dezelfde volgorde als voorheen: dag-geldige opties eerst, de
  // overrides erachter, ontdubbeld op label.
  const rauw = [];
  for (const [gemKey, gemeente] of Object.entries(regels.gemeenten)) {
    if (gemKey.startsWith('_') || gemeente.actief === false) continue;
    const diensten = Object.entries(gemeente.diensten || {}).map(([dienstId, dienst]) => ({
      dienstId,
      dagGeldig:
        !(dienstId === 'dagdienst' && (dag === 'za' || dag === 'zo')) &&
        dienstDagenVoorType(dienst, weekType).includes(dag),
    }));
    if (!diensten.length) continue;

    if (soortNodig(gemeente)) {
      // Elke dienst een eigen regel, mét soort — hier valt er echt iets te kiezen.
      for (const d of diensten) {
        rauw.push({ gemKey, dienstId: d.dienstId, dagGeldig: d.dagGeldig, heleGemeente: false,
          label: dienstLabel(gemeente.naam, d.dienstId, true) });
      }
      continue;
    }

    // Eén regel voor de hele gemeente. Doelvolgorde:
    //   1. de dienst die op DEZE dag geldig is — die bedoelt de gebruiker vrijwel zeker
    //      (Lingewaard op di/do is de dagdienst, op vr/za/zo de late);
    //   2. anders de LATE dienst. Dat geldt voor de dagen waarop géén Lingewaard-dienst
    //      valt (ma/wo): daar geeft de kalender geen uitsluitsel en is dit een KEUZE VAN
    //      EMRE (8 aug 2026), niet iets wat uit de config volgt. Wie hier later iets anders
    //      wil, verandert dus een besluit en geen afleiding;
    //   3. anders de eerste dienst, puur als vangnet.
    // Alle diensten duren 8u en de engine rekent sowieso met een vaste dienstUren = 8, dus
    // deze keuze verschuift geen uren — gemeten op seed én live op 8 aug 2026.
    const doel = diensten.find(d => d.dagGeldig)
      || diensten.find(d => d.dienstId.startsWith('late_dienst'))
      || diensten[0];
    rauw.push({ gemKey, dienstId: doel.dienstId, dagGeldig: doel.dagGeldig, heleGemeente: true,
      label: dienstLabel(gemeente.naam, doel.dienstId, false) });
  }

  // Dag-geldige eerst (stabiel binnen elke groep), daarna ontdubbelen op label — dat laatste
  // vangt twee gemeenten met dezelfde displaynaam af, net als voorheen.
  const opties = [];
  const gezienLabels = new Set();
  for (const alleenDagGeldig of [true, false]) {
    for (const o of rauw) {
      if (o.dagGeldig !== alleenDagGeldig) continue;
      if (gezienLabels.has(o.label)) continue;
      gezienLabels.add(o.label);
      opties.push(o);
    }
  }
  return opties;
}

// Klein dropdown-menu per cel. Via een portal op document.body zodat de overflow
// van de matrix-tabel het menu niet afknipt. Sluit bij klik buiten of Escape.
function CelMenu({ mw, dag, rooster, weekType, regels, weekIndex, weeknummer, wijzigCel, celOverrides, setCelOverride, x, y, celTop, onSluit }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onSluit(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onSluit]);

  // Klaprichting. Pas NA de eerste render te bepalen: de hoogte hangt af van het aantal
  // dienst-opties en of Weghalen/Markering meedoen. useLayoutEffect (niet useEffect) zodat
  // de correctie vóór de paint valt — anders zie je het menu zichtbaar verspringen.
  //
  // scrollHeight, NIET offsetHeight: de maxHeight-clamp hieronder kapt offsetHeight af op
  // precies de ruimte eronder, dus "past het niet?" zou daarmee altijd nee zijn en de flip
  // nooit vuren. scrollHeight geeft de ONgeclampte contenthoogte (± de 2px border, wat voor
  // deze vergelijking niet uitmaakt).
  const menuRef = useRef(null);
  const [naarBoven, setNaarBoven] = useState(false);
  useLayoutEffect(() => {
    const hoogte = menuRef.current?.scrollHeight ?? 0;
    const ruimteOnder = window.innerHeight - y;
    const ruimteBoven = celTop - 8;
    setNaarBoven(hoogte > ruimteOnder && ruimteBoven > ruimteOnder);
  }, [y, celTop]);

  const huidige = vindPlaatsing(rooster, mw.naam, dag);
  const opties = geldigeOptiesVoor(dag, weekType, regels);
  // Zelfde soort-regel als de lijst hieronder, zodat de "Nu: …"-kop en de opties niet
  // uit elkaar lopen (bij Lingewaard staat er dus "Nu: Lingewaard", niet "— dag").
  const huidigeGemeente = huidige ? regels.gemeenten[huidige.gemKey] : null;
  const huidigeLabel = huidige
    ? dienstLabel(huidigeGemeente?.naam || huidige.gemKey, huidige.dienstId, huidigeGemeente ? soortNodig(huidigeGemeente) : true)
    : null;

  const kies = (gemKey, dienstId) => {
    if (huidige && huidige.gemKey === gemKey && huidige.dienstId === dienstId) { onSluit(); return; }
    if (huidige) wijzigCel({ weekIndex, gemKey: huidige.gemKey, dienstId: huidige.dienstId, dag, naam: mw.naam, operatie: 'weghalen' });
    wijzigCel({ weekIndex, gemKey, dienstId, dag, naam: mw.naam, operatie: 'toevoegen' });
    onSluit();
  };
  const weghalen = () => {
    if (huidige) wijzigCel({ weekIndex, gemKey: huidige.gemKey, dienstId: huidige.dienstId, dag, naam: mw.naam, operatie: 'weghalen' });
    onSluit();
  };

  // Handmatige markering (stukje 3/3): override-waarde voor deze cel + setter (sleutel = weeknummer).
  const huidigeMarkering = celOverrides?.[weeknummer]?.[mw.naam]?.[dag] ?? null;
  // Via overrideCode: de waarde kan een kale string zijn ('K'/'S'/'leeg') of, bij Kantoor met
  // een eigen urenaantal, { code: 'K', uren }. Het vinkje hieronder vergelijkt op de CODE.
  const huidigeCode = overrideCode(huidigeMarkering);
  const zetMarkering = (waarde) => { setCelOverride({ weeknummer, naam: mw.naam, dag, waarde }); onSluit(); };

  // Kantooruren. Staat er al een K, dan opent het veld met díé waarde; anders met de
  // standaard. Kantoor slaat NIET meteen op zoals de andere drie: eerst het urenveld tonen,
  // dan Toepassen — anders is het aantal niet mee te geven. School/Leeg/Automatisch houden
  // hun huidige één-klik-gedrag.
  const [kantoorUren, setKantoorUren] = useState(
    () => (huidigeCode === 'K' ? overrideUren(huidigeMarkering) : KANTOOR_UREN_STANDAARD),
  );
  const [toonKantoorUren, setToonKantoorUren] = useState(huidigeCode === 'K');
  const bevestigKantoor = () => {
    // Buiten bereik of onleesbaar → de standaard. Het invoerveld begrenst al, maar een
    // getypte waarde komt er ongefilterd doorheen en die hoort niet in de opslag te landen.
    const n = Number(kantoorUren);
    const geldig = Number.isFinite(n) && n >= KANTOOR_UREN_MIN && n <= KANTOOR_UREN_MAX;
    setCelOverride({
      weeknummer, naam: mw.naam, dag,
      waarde: 'K',
      uren: geldig ? n : KANTOOR_UREN_STANDAARD,
    });
    onSluit();
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-40" onClick={onSluit} />
      {/* Standaard hangt het menu ónder de cel (top: y). Past het daar niet en is er boven méér
          ruimte, dan klapt het om: het anker wordt de BOVENkant van de cel en translateY(-100%)
          laat het omhoog groeien. Beide richtingen houden een maxHeight + overflowY als vangnet,
          zodat het menu ook bij weinig ruimte binnen beeld blijft (zelfde clamp-aanpak als
          MeldingBel en de modals). `y` en `celTop` zijn viewport-relatief (getBoundingClientRect)
          en het menu is position: fixed, dus die rekensommen kloppen zonder omrekening. */}
      <div
        ref={menuRef}
        className="fixed z-50 bg-white border border-slate-200 rounded-lg shadow-lg py-1"
        style={naarBoven
          ? { top: celTop - 4, left: x, transform: 'translate(-50%, -100%)', minWidth: 160, maxHeight: `${celTop - 8}px`, overflowY: 'auto' }
          : { top: y, left: x, transform: 'translateX(-50%)', minWidth: 160, maxHeight: `calc(100vh - ${y + 8}px)`, overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 border-b border-slate-100">
          {mw.naam} · {DAG_LANG[dag]}
          {huidigeLabel && <span className="block font-normal text-slate-400 mt-0.5" style={{ fontSize: 10 }}>Nu: {huidigeLabel}</span>}
        </div>
        {opties.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Geen dienst op deze dag</div>}
        {opties.map(o => {
          // Bij een samengevouwen regel staat het vinkje bij de GEMEENTE: welke dienst er
          // ook op de cel staat, deze ene optie representeert ze allemaal.
          const actief = !!huidige && huidige.gemKey === o.gemKey
            && (o.heleGemeente || huidige.dienstId === o.dienstId);
          return (
            <button
              key={`${o.gemKey}|${o.dienstId}`}
              onClick={() => kies(o.gemKey, o.dienstId)}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between gap-3 hover:bg-slate-50 ${actief ? 'text-brand-700 font-semibold' : 'text-slate-700'}`}
            >
              <span>{o.label}</span>
              {actief && <Check className="w-3 h-3 shrink-0" />}
            </button>
          );
        })}
        {huidige && (
          <button
            onClick={weghalen}
            className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 border-t border-slate-100 flex items-center gap-1.5"
          >
            <Trash2 className="w-3 h-3 shrink-0" /> Weghalen
          </button>
        )}
        {/* ALTIJD tonen, ook als de medewerker deze dag in het rooster staat. Een
            'leeg'-override wint in getCel (roostercodes.js:422) van de roosterplaatsing,
            dus de cel is dan leeg terwijl dit menu via vindPlaatsing nog "Nu: ..." toont.
            Achter een {!huidige}-gate was die override onzichtbaar én niet meer te wissen. */}
        <div className="border-t border-slate-100 mt-1 pt-1">
          <div className="px-3 py-1 text-xs font-semibold text-slate-500">Markering</div>
          {[
            { waarde: 'S', label: 'School' },
            { waarde: 'K', label: 'Kantoor' },
            { waarde: 'leeg', label: 'Leeg' },
            { waarde: null, label: 'Automatisch' },
          ].map(m => {
            const actief = huidigeCode === m.waarde;
            return (
              <button
                key={m.label}
                onClick={() => (m.waarde === 'K' ? setToonKantoorUren(true) : zetMarkering(m.waarde))}
                className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between gap-3 hover:bg-slate-50 ${actief ? 'text-brand-700 font-semibold' : 'text-slate-700'}`}
              >
                <span>{m.label}</span>
                {actief && <Check className="w-3 h-3 shrink-0" />}
              </button>
            );
          })}
          {/* Urenveld: alleen bij Kantoor. School, Leeg en Automatisch krijgen het niet —
              school houdt zijn eigen regeling (mw.school.uren) en de andere twee hebben
              geen uren. Enter doet hetzelfde als Toepassen. */}
          {toonKantoorUren && (
            <div className="px-3 pt-2 pb-1 border-t border-slate-100 mt-1">
              <div className="text-xs text-slate-500 mb-1">Kantooruren</div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={KANTOOR_UREN_MIN}
                  max={KANTOOR_UREN_MAX}
                  step={KANTOOR_UREN_STAP}
                  value={kantoorUren}
                  onChange={(e) => setKantoorUren(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') bevestigKantoor(); }}
                  className="w-16 border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
                <span className="text-xs text-slate-400">uur</span>
                <button
                  onClick={bevestigKantoor}
                  className="ml-auto text-xs font-semibold text-brand-700 hover:underline"
                >
                  Toepassen
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

// ── Herkomst-teken: welke cel is HANDMATIG gemarkeerd? ──────────────────────
//
// Eén teken met één betekenis: deze cel draagt een handmatige S/K/leeg-markering uit
// celOverrides. Hier stond eerder ook een teken voor goedgekeurde ruilen, maar twee tekens
// van een paar pixels bleken in een matrix van 34px-kolommen niet uit elkaar te houden
// zonder te hoveren, en bij meerdere ruilen in één week werd het rommelig. Ruilen staan al
// in het Ruilverzoeken-scherm; hier is één ondubbelzinnig teken meer waard dan twee die op
// elkaar lijken.
//
// Vorm: een accentbalk langs de LINKERKANT van de cel, geen hoekje. Een balk over de volle
// hoogte valt op bij het scannen van een kolom en blijft even zichtbaar op een LEGE cel — wat
// telt, want de markering 'leeg' maakt de cel juist leeg. De badge-code staat gecentreerd en
// wordt niet geraakt.
//
// Puur weergave: geen engine, geen server, geen opslag.
const HERKOMST_MARKERING_KLEUR = '#334155'; // leisteen-700: neutraal, claimt geen dienst-betekenis
const HERKOMST_BALK_PX = 3;                 // breedte van de accentbalk

const MARKERING_LABEL = { S: 'School', K: 'Kantoor', leeg: 'Leeg' };

/**
 * Tooltip van één cel. Begint altijd met de bestaande klik-aanwijzing, zodat een cel zonder
 * markering letterlijk houdt wat hij had.
 */
function celHerkomstTitel(markering) {
  if (!markering) return 'Klik om te wijzigen';
  // Via overrideCode: de waarde kan sinds het instelbare urenaantal ook een object zijn, en
  // dat zou hier anders als "[object Object]" in de tooltip belanden.
  const code = overrideCode(markering);
  // De uren staan ALTIJD in de tooltip als het om Kantoor gaat, ook bij 8 — de badge in de
  // cel toont ze alleen bij een afwijkend aantal (te weinig ruimte), dus dit is de plek waar
  // je het getal met zekerheid kunt nalezen.
  const uren = code === 'K' ? ` (${urenKort(overrideUren(markering))} uur)` : '';
  return `Klik om te wijzigen\n\nHandmatige markering: ${MARKERING_LABEL[code] ?? code}${uren}`;
}

// ── Ruil-markering: er ligt een goedgekeurde ruil op deze cel ────────────────
// De matrix leest maandroosters, waar de ruil NIET in verwerkt is; de server legt hem bij
// het lezen over vastgelegdeWeken (rooster-service.js:130-150), en dat is wat het team ziet.
// Zonder teken past de teamleider een cel aan zonder te weten dat het effect voor het team
// een ander is. We tonen dus ALLEEN dat er geruild is — de uitkomst blijft van de server.
//
// BEWUST EEN ANDERE VORM ÉN PLEK dan de herkomst-balk hierboven: die is een volle-hoogte
// balk LINKS, dit is een driehoekje in de hoek RECHTSBOVEN. Bij de vorige poging sneuvelden
// twee varianten omdat ze zonder hoveren niet uit elkaar te houden waren; twee tekens die op
// elkaar lijken zijn erger dan één. Liggen ze allebei op één cel, dan blijven ze naast elkaar
// zichtbaar en heeft elk zijn eigen tooltip.
//
// De kleur is die van legenda-code G ("Geruild", roostercodes.js:16) — dat is de bestaande
// betekenis-kleur voor ruilen in deze app, geen nieuwe.
const RUIL_MARKERING_KLEUR = '#9d174d';
const RUIL_HOEK_PX = 8;

// Tooltip van de ruil-markering. Drie dingen, in gewone taal: dat er geruild is en tussen
// wie (in de richting die op DEZE cel geldt), wie het team ziet, en waar je het controleert.
function ruilTitel(ruil) {
  if (!ruil) return null;
  const basis = `Geruild — ${ruil.oud} → ${ruil.nieuw}. Het team ziet ${ruil.nieuw} op deze dienst. Zie Teamrooster.`;
  // Meer dan één wissel op dezelfde dienst: de uiteindelijke naam volgt uit de ketting, en
  // die rekenen we hier bewust niet uit (dat zou pasRuilWisselsToe nabouwen). Dan verwijzen
  // we naar de enige bron die het wél weet.
  return ruil.aantal > 1
    ? `${basis}\n\nLet op: er liggen meer ruilen op deze dienst — Teamrooster is leidend.`
    : basis;
}

// ── Handmatig-teken: hier staat iets anders dan de engine plande ─────────────
// Een zelf aangepaste cel is niet van een geplande te onderscheiden; bij het opschonen van
// september waren de eigen correcties achteraf niet terug te vinden (§42q). Het
// wijzigingslogboek weet het wel — zie lib/handmatigmarkering.js, dat is een pure opzoeking
// tegen store.wijzigingen en verandert niets aan de data.
//
// HET DERDE TEKEN, dus DRIE keer anders dan de andere twee. De les uit de herkomst-balk en
// het ruil-teken hierboven is dat twee tekens van een paar pixels in kolommen van 34px niet
// uit elkaar te houden waren zonder te hoveren; met een derde erbij telt dat dubbel. Daarom:
//   VORM   — een RONDE stip. De herkomst-balk is een rechte lijn, het ruil-teken een scherpe
//            hoek; rond is de enige vorm die op deze afmeting meteen anders leest dan beide.
//   PLEK   — RECHTSONDER. De balk zit links over de volle hoogte, de driehoek rechtsboven.
//            Rechtsonder is de enige hoek waar niets staat: er is geen ander absoluut
//            gepositioneerd element met `bottom`, dus de drie kunnen naast elkaar staan.
//   KLEUR  — #0891b2 (cyaan-600). Bewust een kleur die NERGENS in roostercodes.js voorkomt,
//            niet als legenda-code (CEL) en niet in het dienst-palet (PALET): een kleur die
//            al iets betekent zou dit teken als een dienstsoort laten lezen. Gecontroleerd:
//            geen enkele treffer op '0891b2' in client/src. Ook qua toon anders dan de andere
//            twee, die allebei donker en gedempt zijn (#334155 leisteen, #9d174d pruim).
//            ⚠️ Cyaan staat wél als OPTIE in het palet (roostercodes.js:46); zou een dienst
//            die kleur ooit krijgen, dan is dit teken minder onderscheidend. Op de huidige
//            data gebruikt geen enkele dienst hem (alleen blauw en geel zijn in gebruik).
const HANDMATIG_MARKERING_KLEUR = '#0891b2';
const HANDMATIG_STIP_PX = 6;

// De tooltipzin zelf staat in lib/handmatigLabels.js — pure tekst, geen JSX, zodat de
// diagnostiek precies dezelfde zin kan afdrukken als wat hier gerenderd wordt (node kan geen
// .jsx importeren). Zelfde reden als bij lib/fteLabels.js.

// ── MATRIX ───────────────────────────────────────────────────────────────────

function RoosterMatrix({ rooster, medewerkers, weeknummer, jaar, bijzonderheden, setBijzonderheid, weekIndex, regels, wijzigCel, afwTypeWeek, celOverrides, setCelOverride, vakantieDagen = null, ruilWissels = [], wijzigingen = [], isoJaar = null }) {
  const maandagMatrix = weekNaarMaandag(weeknummer, jaar);
  // Kolomvolgorde op naam, niet op configbestand-volgorde — zelfde ordening als de
  // balans-tabel eronder, zodat je een medewerker in beide op dezelfde plek zoekt.
  const mwKeys = Object.keys(medewerkers)
    .filter(k => !k.startsWith('_') && isRoosterbaar(medewerkers[k]))
    .sort((a, b) => medewerkers[a].naam.localeCompare(medewerkers[b].naam, 'nl'));
  const notities = bijzonderheden?.[weeknummer] || {};
  const weekType = schoolweekType(weeknummer);
  const [openCel, setOpenCel] = useState(null); // { celId, x, y } voor het cel-menu

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="text-xs border-collapse min-w-full">
        <thead>
          <tr className="border-b border-slate-100">
            <th className="w-10 shrink-0 bg-white sticky left-0 z-10" />
            {mwKeys.map(k => (
              <th key={k} className="text-center pb-1 pt-2 px-0.5" style={{ minWidth: 34 }}>
                <span
                  className="text-slate-600 font-medium leading-none"
                  style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', display: 'inline-block', fontSize: 11 }}
                >
                  {medewerkers[k].naam}
                </span>
              </th>
            ))}
            <th
              className="sticky right-0 bg-white z-10 text-left pl-3 pr-4 text-slate-500 font-medium whitespace-nowrap"
              style={{ minWidth: 220, borderLeft: '1px solid #e2e8f0' }}
            >
              Bijzonderheden
            </th>
          </tr>
        </thead>
        <tbody>
          {DAGEN.map(dag => (
            <tr key={dag} className="border-t border-slate-100 hover:bg-slate-50/50">
              <td
                className={`py-1.5 pl-3 pr-2 font-semibold sticky left-0 bg-white z-10 ${
                  dag === 'za' || dag === 'zo' ? 'text-violet-700' : 'text-slate-700'
                }`}
                style={{ fontSize: 11, borderRight: '1px solid #e2e8f0' }}
              >
                <div>{DAG_KORT[dag]}</div>
                <div className="font-normal text-slate-400" style={{ fontSize: 9 }}>
                  {(() => { const d = new Date(maandagMatrix); d.setDate(maandagMatrix.getDate() + DAGEN.indexOf(dag)); return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }); })()}
                </div>
              </td>
              {mwKeys.map(k => {
                const mw = medewerkers[k];
                // Eén lookup, twee afnemers: getCel (onveranderd doorgegeven) en het herkomst-teken.
                // undefined = geen markering — 'Automatisch' VERWIJDERT de sleutel
                // (useRoosterStore.js:189-200), dus er blijft niets truthy over en er komt geen teken.
                const markering = celOverrides?.[weeknummer]?.[mw.naam]?.[dag];
                const cel = getCel(mw.naam, dag, rooster, mw, weekType, weeknummer, afwTypeWeek?.[mw.naam], markering, regels, vakantieDagen);
                // Een kantoormarkering met een AFWIJKEND urenaantal krijgt het getal naast de
                // code. Bij 8 niet: dat is de standaard en zou elke K-cel breder maken voor
                // niets. De cel is 28px, dus alleen het getal — de eenheid staat in de tooltip.
                const kantoorUrenCel = overrideCode(markering) === 'K' ? overrideUren(markering) : null;
                const toonKantoorUrenCel = kantoorUrenCel !== null && kantoorUrenCel !== KANTOOR_UREN_STANDAARD;
                // Ligt er een goedgekeurde ruil op de dienst waar DEZE persoon deze dag staat?
                // Twee stappen, want een wissel wijst een SLOT aan (gemeente/dienst/dag) en de
                // matrix een PERSOON: eerst opzoeken waar hij staat (vindPlaatsing),
                // dan het slot vergelijken. De naam-check erna is essentieel — een cel kan
                // meerdere personen bevatten en alleen degene die wordt weggeruild (ruil.oud)
                // hoort het teken te krijgen, niet zijn collega in hetzelfde slot.
                const plaatsing = vindPlaatsing(rooster, mw.naam, dag);
                const ruilSlot = plaatsing
                  ? ruilOpCel(ruilWissels, isoJaar, weeknummer, plaatsing.gemKey, plaatsing.dienstId, dag)
                  : null;
                const ruil = ruilSlot && ruilSlot.oud === mw.naam ? ruilSlot : null;
                const ruilTekst = ruilTitel(ruil);
                // Is hier met de hand ingegrepen? BEWUST NIET via vindPlaatsing: die zoekt de
                // naam ín het rooster, en na een 'weghalen' staat hij daar juist niet meer —
                // dan zou het teken precies ontbreken waar het het meest zegt. De opzoeking
                // joint daarom rechtstreeks op (isoJaar, weeknummer, dag, naam) en haalt
                // gemeente/dienst uit het RECORD (handmatigmarkering.js), niet uit deze cel.
                // `rooster` gaat mee omdat het teken pas telt als het saldo nog klopt met wat
                // er NU staat: een herstel of een nieuwe generatie draait de plaatsing terug
                // zonder het record te wissen, en zonder die toets wijst de stip naar een
                // ingreep die niet meer bestaat (Bram wk33/ma, 5 aug).
                const handmatig = handmatigOpCel(wijzigingen, rooster, isoJaar, weeknummer, dag, mw.naam);
                const handmatigTekst = handmatigTitel(handmatig, regels);
                // Eén title voor de hele cel, zodat alle betekenissen bereikbaar blijven als er
                // meer dan één teken ligt. De driehoek en de stip hebben daarnaast hun eigen
                // title met alleen hun eigen tekst.
                const titel = [celHerkomstTitel(markering), ruilTekst, handmatigTekst].filter(Boolean).join('\n\n');
                const celId = `${k}|${dag}`;
                return (
                  <td key={k} className="py-1.5 px-0.5 text-center" title={titel}>
                    <button
                      type="button"
                      title={titel}
                      onClick={(e) => {
                        if (openCel?.celId === celId) { setOpenCel(null); return; }
                        const r = e.currentTarget.getBoundingClientRect();
                        // celTop erbij: CelMenu heeft de BOVENkant van de cel nodig om naar
                        // boven te kunnen klappen als er onderaan te weinig ruimte is.
                        setOpenCel({ celId, x: r.left + r.width / 2, y: r.bottom + 4, celTop: r.top });
                      }}
                      className="inline-flex items-center justify-center rounded cursor-pointer hover:bg-slate-100"
                      style={{ minWidth: 28, height: 18, padding: 0, border: 'none', background: 'transparent', position: 'relative' }}
                    >
                      {cel ? (
                        <span
                          className="inline-block rounded font-bold text-center leading-tight"
                          style={{ minWidth: 28, boxSizing: 'border-box', padding: '2px 3px', background: cel.bg, color: cel.fg, fontSize: 10 }}
                        >
                          {cel.code}
                          {toonKantoorUrenCel && (
                            <span style={{ fontSize: 8, marginLeft: 1 }}>{urenKort(kantoorUrenCel)}</span>
                          )}
                        </span>
                      ) : (
                        <span className="inline-block" style={{ width: 28 }} />
                      )}
                      {/* Accentbalk links = handmatig gemarkeerd. Over de volle hoogte van de cel,
                          dus ook zichtbaar wanneer de markering 'leeg' is en er geen badge staat.
                          aria-hidden omdat de betekenis al in het title-attribuut staat. */}
                      {markering && (
                        <span
                          aria-hidden="true"
                          style={{
                            position: 'absolute', top: 0, bottom: 0, left: 0,
                            width: HERKOMST_BALK_PX, borderRadius: 2,
                            background: HERKOMST_MARKERING_KLEUR,
                          }}
                        />
                      )}
                      {/* Ruil-teken = driehoekje RECHTSBOVEN. Andere vorm, andere hoek en een
                          andere kleur dan de herkomst-balk hiernaast, zodat de twee ook zonder
                          hoveren uit elkaar te houden zijn en naast elkaar kunnen staan.
                          Eigen title: hoveren op het teken geeft alleen de ruil-tekst, hoveren
                          op de cel geeft beide. Geen aria-hidden, want dit is de enige plek
                          waar deze betekenis staat. */}
                      {ruil && (
                        <span
                          title={ruilTekst}
                          style={{
                            position: 'absolute', top: 0, right: 0,
                            width: 0, height: 0,
                            borderTop: `${RUIL_HOEK_PX}px solid ${RUIL_MARKERING_KLEUR}`,
                            borderLeft: `${RUIL_HOEK_PX}px solid transparent`,
                          }}
                        />
                      )}
                      {/* Handmatig-teken = ronde stip RECHTSONDER. Enige ronde vorm, enige
                          hoek met `bottom`, en een kleur die nergens anders iets betekent —
                          zo blijven de drie tekens ook naast elkaar uit elkaar te houden.
                          Eigen title, net als de driehoek hierboven. */}
                      {handmatig && (
                        <span
                          title={handmatigTekst}
                          style={{
                            position: 'absolute', bottom: 0, right: 0,
                            width: HANDMATIG_STIP_PX, height: HANDMATIG_STIP_PX,
                            borderRadius: '50%',
                            background: HANDMATIG_MARKERING_KLEUR,
                          }}
                        />
                      )}
                    </button>
                    {openCel?.celId === celId && (
                      <CelMenu
                        mw={mw} dag={dag} rooster={rooster} weekType={weekType} regels={regels}
                        weekIndex={weekIndex} weeknummer={weeknummer} wijzigCel={wijzigCel}
                        celOverrides={celOverrides} setCelOverride={setCelOverride}
                        x={openCel.x} y={openCel.y} celTop={openCel.celTop} onSluit={() => setOpenCel(null)}
                      />
                    )}
                  </td>
                );
              })}
              <td className="py-1 pl-3 pr-3 sticky right-0 bg-white z-10" style={{ borderLeft: '1px solid #e2e8f0' }}>
                <textarea
                  value={notities[dag] || ''}
                  onChange={e => setBijzonderheid(weeknummer, dag, e.target.value)}
                  placeholder={`Notitie ${DAG_KORT[dag]}...`}
                  rows={1}
                  className="w-full text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-500 text-slate-700 bg-white resize-y leading-snug align-top"
                  style={{ minWidth: 200, minHeight: '2rem' }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── DAG-WEERGAVE ─────────────────────────────────────────────────────────────

function DagWeergave({ rooster, medewerkers, weeknummer, jaar, afwTypeWeek = {}, celOverrides, regels, vakantieDagen = null }) {
  const maandag = weekNaarMaandag(weeknummer, jaar);
  const weekType = schoolweekType(weeknummer);
  const codeVan = (mw, dag) => getCel(mw.naam, dag, rooster, mw, weekType, weeknummer, afwTypeWeek?.[mw.naam], celOverrides?.[weeknummer]?.[mw.naam]?.[dag], regels, vakantieDagen)?.code;
  // De dienst-rijen lezen rauw uit het rooster (zie hieronder) en gaan dus NIET door getCel
  // heen, waar afwezigheid normaal voorrang krijgt. Daarom hier hetzelfde type-label én
  // dezelfde kleur als in TeamRooster/Mijn Rooster, via afwezigStijl: de naam blijft staan
  // (hij is ingepland), maar doorgestreept in de kleur van het type.

  // Roosterbare medewerkers van deze omgeving — bron voor de School/Kantoor-rijen
  // én voor de vraag of die rijen überhaupt zin hebben.
  const roosterbaar = Object.values(medewerkers).filter(mw => !mw._comment && isRoosterbaar(mw));

  // Dienst-rijen: één rij per cel-code, afgeleid uit de diensten die er zijn. Een rij
  // blijft staan als hij leeg is (streepjes) — dan zie je juist dát een dienst niet
  // gevuld is. De namen komen per dag uit alle diensten die deze code delen. Het
  // rooster gaat mee zodat een dienst die intussen uit regels is verwijderd maar nog
  // in een vastgelegde week staat, niet stil uit deze tabel valt.
  const dienstRijen = dienstGroepenPerCode(regels, rooster).map(groep => ({
    id: `code-${groep.cel.code}`,
    label: groep.cel.label,
    cel: groep.cel,
    get: dag => groep.diensten.flatMap(({ gemKey, dienstId }) => rooster?.[gemKey]?.[dienstId]?.[dag] || []),
  }));

  // School/Kantoor komen NIET uit het rooster maar uit de medewerker-config. Een lege
  // rij is daar dus ruis in plaats van informatie (anders dan bij een dienst-rij):
  // alleen tonen als iemand het ook echt heeft ingesteld.
  const heeftSchool  = roosterbaar.some(mw => mw.school?.dag);
  const heeftKantoor = roosterbaar.some(mw => mw.kantoor_dagen?.length);

  const rijen = [
    ...dienstRijen,
    ...(heeftSchool ? [{
      id: 'school', label: 'School', cel: CEL.S,
      // Alleen tonen in de werktijd-week (week A); in week B telt school als eigen tijd.
      // Op naam gesorteerd: deze rij loopt over de medewerker-config, niet over het
      // rooster, dus zonder sort zou hier de configbestand-volgorde staan.
      get: dag => roosterbaar.filter(mw => codeVan(mw, dag) === 'S')
        .map(mw => mw.naam).sort((a, b) => a.localeCompare(b, 'nl')),
    }] : []),
    ...(heeftKantoor ? [{
      id: 'kantoor', label: 'Kantoor', cel: CEL.K,
      // Zelfde reden als bij School: config-bron, dus expliciet op naam sorteren.
      get: dag => roosterbaar.filter(mw => codeVan(mw, dag) === 'K')
        .map(mw => mw.naam).sort((a, b) => a.localeCompare(b, 'nl')),
    }] : []),
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
      <table className="w-full text-xs border-collapse" style={{ minWidth: 700 }}>
        <thead>
          <tr className="border-b border-slate-100">
            <th className="text-left py-3 pl-4 pr-3 w-28 text-slate-500 font-medium">Dienst</th>
            {DAGEN.map(dag => {
              const datum = new Date(maandag);
              datum.setDate(maandag.getDate() + DAGEN.indexOf(dag));
              const datumStr = datum.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
              return (
                <th
                  key={dag}
                  className={`text-center py-3 px-2 font-medium ${dag === 'za' || dag === 'zo' ? 'text-violet-700 bg-violet-50/40' : 'text-slate-700'}`}
                  style={{ minWidth: 110 }}
                >
                  <div>{DAG_KORT[dag]}</div>
                  <div className="font-normal text-slate-400" style={{ fontSize: 10 }}>{datumStr}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rijen.map(rij => (
            <tr key={rij.id} className="border-t border-slate-100 align-top">
              <td className="py-2.5 pl-4 pr-3 whitespace-nowrap">
                <span
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-semibold"
                  style={{ background: rij.cel.bg, color: rij.cel.fg, fontSize: 11 }}
                >
                  {rij.cel.code}
                  <span className="font-normal opacity-75">{rij.label}</span>
                </span>
              </td>
              {DAGEN.map(dag => {
                const namen = rij.get(dag);
                const isWeekend = dag === 'za' || dag === 'zo';
                return (
                  <td key={dag} className={`py-2.5 px-2 text-slate-700 leading-relaxed ${isWeekend ? 'bg-violet-50/40' : ''}`}>
                    {namen.length > 0
                      ? namen.map((n, i) => {
                          const t = afwTypeWeek?.[n]?.[dag];
                          if (t) {
                            const { label, kleur } = afwezigStijl(t);
                            return (
                              <span key={i} className="block line-through" style={{ fontSize: 11, color: kleur }}>
                                {n}<span className="no-underline"> · {label}</span>
                              </span>
                            );
                          }
                          return <span key={i} className="block" style={{ fontSize: 11 }}>{n}</span>;
                        })
                      : <span className="text-slate-200" style={{ fontSize: 11 }}>—</span>
                    }
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── UREN BALANS (week-modus: 4-weken venster; maand-modus: per blok) ─────────

// Alle vier de kolommen worden VERS berekend (lib/balans.js). `samenvatting` is niet meer de
// bron van de cijfers en ook niet van de rij-set: hij dient nog uitsluitend als terugval voor
// Gepland als een blokweek niet gegenereerd is (bevrorenCumUren).
//
// De rij-set komt uit regels.medewerkers, niet uit de samenvatting. Die samenvatting is
// bevroren bij het genereren, dus iemand die daarna vertrok stond er nog in en iemand die
// erbij kwam ontbrak. Zelfde filter als overal elders: geen underscore-sleutels, geen
// teamleider-only (isRoosterbaar, lib/rollen.js).
function BalansTabel({
  samenvatting, aantalWeken = 4, titel = null, markeringDelta = {},
  blokWeken = [], isoJaar, maandroosters, vastgelegdeWeken, afwezigheid, regels, vakanties,
}) {
  const rijen = Object.entries(regels?.medewerkers || {})
    .filter(([k, mw]) => !k.startsWith('_') && isRoosterbaar(mw))
    .map(([k, mw]) => ({ k, mw }))
    .sort((a, b) => a.mw.naam.localeCompare(b.mw.naam, 'nl'));

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
      <div className="px-5 py-3 border-b border-slate-100">
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{titel || `Uren Balans (${aantalWeken} weken)`}</span>
      </div>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="border-b border-slate-100 text-slate-500">
            <th className="text-left py-2 pl-4 pr-3 font-medium">Medewerker</th>
            <th className="text-right py-2 px-3 font-medium">Contract ({aantalWeken}×)</th>
            <th className="text-right py-2 px-3 font-medium">Gepland</th>
            <th className="text-left py-2 px-3 font-medium">Balans</th>
          </tr>
        </thead>
        <tbody>
          {rijen.map(({ k, mw }) => {
            const r = versBalansVoorBlok({
              blokWeken, isoJaar, mwKey: k, mw,
              maandroosters, vastgelegdeWeken, afwezigheid, regels, vakanties,
              markeringDelta: markeringDelta[mw.naam] || 0,
              bevrorenCumUren: samenvatting?.[k]?.cumUren ?? 0,
            });
            const balans = r.afwijking;
            // Kleur, vinkje én de tekst volgen de GETOONDE waarde, niet de rauwe. Sinds een
            // handmatig verloftotaal over een blokgrens verdeeld kan worden, is `balans` een
            // gebroken getal; 0,04u hoort dan als ±0u groen te zijn en niet als "+0u" in het
            // rood. De exacte waarde blijft opvraagbaar in de tooltip hieronder.
            const balansGetoond = urenAfgerond(balans);
            const ok = balansGetoond === 0;
            const kleur = ok ? 'text-emerald-600' : 'text-red-600';
            const onvolledig = r.ontbrekendeWeken.length > 0;
            return (
              <tr key={k} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="py-2 pl-4 pr-3 font-medium text-slate-700">
                  {mw.naam}
                  {onvolledig && (
                    <span
                      className="ml-1.5 font-normal text-slate-400"
                      style={{ fontSize: 10 }}
                      title={`week ${r.ontbrekendeWeken.join(', ')} niet gegenereerd — Gepland komt uit de laatste generatie`}
                    >
                      onvolledig
                    </span>
                  )}
                </td>
                <td className="py-2 px-3 text-right text-slate-500">{urenNet(r.target)}</td>
                <td className="py-2 px-3 text-right text-slate-700 font-medium">{urenNet(r.gepland)}</td>
                {/* De exacte waarde blijft opvraagbaar in de tooltip: het getal op het scherm
                    is afgerond, en bij een verloftotaal over een blokgrens scheelt dat echt iets. */}
                <td className={`py-2 px-3 font-semibold ${kleur}`} title={`exact: ${balans}u`}>
                  {ok ? '±0u' : balansGetoond > 0 ? `+${urenNet(balans)}` : urenNet(balans)}
                  {ok && <span className="ml-1 text-emerald-400">✓</span>}
                  {r.afwUren > 0 && (
                    <span className="block font-normal text-slate-400" style={{ fontSize: 10 }}>
                      {Object.values(r.afwPerType).some(u => u > 0)
                        ? Object.entries(r.afwPerType).filter(([, u]) => u > 0).map(([t, u]) => `${urenNet(u)} ${t}`).join(', ')
                        : `${urenNet(r.afwUren)} afwezig`}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── LEGENDA ──────────────────────────────────────────────────────────────────

function Legenda({ regels }) {
  // De codes die in DEZE omgeving echt voorkomen (elke dienst via celCodeVoorDienst,
  // daarna S/K/V/Z/A), plus de gereserveerde RI/G/OW die alleen op het scherm staan.
  // Met de huidige data is dat exact dezelfde twaalf in dezelfde volgorde als eerst.
  const items = [...legendaCodesVoor(regels), ...LEGENDA_RESERVE.map(code => CEL[code])];
  return (
    <div className="bg-white rounded-xl border border-slate-200 px-5 py-4">
      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Legenda</div>
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {items.map(item => (
          <div key={item.code} className="flex items-center gap-1.5">
            <span
              className="inline-block rounded font-bold text-center"
              style={{ width: 26, padding: '2px 0', fontSize: 10, background: item.bg, color: item.fg }}
            >
              {item.code}
            </span>
            <span className="text-xs text-slate-600">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── KNELPUNTEN-ANALYSE ───────────────────────────────────────────────────────

// BEWUST NIET DE GEDEELDE DAG_LANG (data/afwezigheid.js). Deze variant staat MIDDEN in een
// zin — "Bram heeft maandag niets" — waar de hoofdletter van Maandag fout leest. Zeven waarden
// die alleen in hun eerste letter verschillen, dus samenvoegen zou hier stil een hoofdletter
// midden in een zin zetten. Wie ze ooit tóch wil koppelen: afleiden met toLowerCase(), niet
// de ene door de andere vervangen.
const DAG_LANG_KN = { ma: 'maandag', di: 'dinsdag', wo: 'woensdag', do: 'donderdag', vr: 'vrijdag', za: 'zaterdag', zo: 'zondag' };

// "3 weken". Deze meldingen bestaan alleen bij MINSTENS twee weken (de conflictWeken-poort
// in analyseKnelpunten), dus enkelvoud kan nu niet voorkomen — de tak staat er wel, zodat
// de zin niet stil omvalt als die drempel ooit wijzigt.
const wekenLabel = (n) => `${n} ${n === 1 ? 'week' : 'weken'}`;

function isIngeplandOpDag(naam, dag, rooster) {
  return Object.values(rooster).some(gemR =>
    Object.values(gemR).some(dagmap => Array.isArray(dagmap[dag]) && dagmap[dag].includes(naam))
  );
}

/**
 * Kan deze persoon dit slot deze week draaien? Voedt de "vrije medewerker"-suggestie.
 *
 * De persoon/dag/gemeente-regels komen UIT DE ENGINE, via isBeschikbaar. Hier stond
 * eerder een eigen nabouw van diezelfde regels, en die was op zes punten uit de pas
 * gelopen — drie ervan gaven een VERKEERDE suggestie op de huidige data:
 *   - vaste_vrije_dagen werd niet gecontroleerd (Bram is ma-do structureel vrij en
 *     werd toch voorgesteld; de werkdagen-check ving dat niet op, want die lijst is leeg)
 *   - de weekend-exclusiviteit van vast_weekend_gemeente ontbrak (Bram/Leroy/Bayram/
 *     Jermaine kwamen in beeld voor weekendslots buiten Rheden)
 *   - weekend_groep stond hardgecodeerd op A, dus voor groep B (Omar/Kimmy/Bayram/
 *     Martijn) stond de rotatie precies omgekeerd
 * en twee lieten een suggestie liggen: de vervallen schooldag in een vakantieweek
 * (schoolVervalt, rooster-engine.js:80) en afwezigheid die per WEEK werd getoetst in
 * plaats van per DAG.
 *
 * Wat hieronder blijft staan is precies wat isBeschikbaar NIET kan: hij krijgt geen
 * dienstId mee, dus alles wat van de dienst afhangt hoort hier.
 *
 * mwKey en weekType worden in de body van isBeschikbaar (rooster-engine.js) nergens gelezen, maar
 * ze gaan bewust echt mee in plaats van als dummy: gaat die body ze ooit wél gebruiken,
 * dan werkt dit vanzelf mee in plaats van stil verkeerd.
 */
function isVrijEnGeschikt(mwKey, mw, dag, gemKey, dienstId, weekRooster, weeknummer, afwMap, weekType, vakantieDagen) {
  if (!isBeschikbaar(mwKey, mw, dag, gemKey, afwMap, weekType, weeknummer, vakantieDagen)) return false;

  // Lingewaard-vastpatroon (Haruun/Myrthe): nooit voorstellen op een ander-gemeente-weekend
  if (mw.vast_rotatiepatroon && (dag === 'za' || dag === 'zo') && gemKey !== 'lingewaard') return false;

  const isLaat = dienstId !== 'dagdienst' && dienstId !== 'flexibel';
  const isDag  = dienstId === 'dagdienst';
  if (isLaat && mw.alleen_dagdienst) return false;
  if (isLaat && mw.geen_late_dienst_dagen?.includes(dag)) return false;
  if (isDag  && mw.geen_dagdienst)   return false;
  if (isDag  && (dag === 'za' || dag === 'zo')) return false;

  return !isIngeplandOpDag(mw.naam, dag, weekRooster);
}

// `bronnen` levert wat de VERSE balans nodig heeft (maandroosters, vastgelegdeWeken, vakanties,
// celOverrides). Zonder die vier zou het balans-advies weer uit samenvattingPerBlok moeten lezen
// en kan het van de tabel afwijken — precies wat we hier dichtzetten. `weekAnalyses` hoort in
// datzelfde rijtje en om dezelfde reden: vers boven bevroren — zie de lus hieronder.
function analyseKnelpunten(maandrooster, regels, afwezigheid, jaar, bronnen = {}) {
  if (!maandrooster?.weken?.length) return [];

  const { maandroosters, vastgelegdeWeken, vakanties, celOverrides, weekAnalyses = {} } = bronnen;
  const { weken, samenvatting } = maandrooster;
  const mwrs = regels.medewerkers;
  const mwKeys = Object.keys(mwrs).filter(k => !k.startsWith('_') && isRoosterbaar(mwrs[k]));
  const suggesties = [];

  // 1. Groepeer conflicten per slot (gemKey|dienst|dag)
  //
  // VERS uit weekAnalyses, NIET uit week.conflicten. Dat opgeslagen veld is afwezigheids-BLIND.
  // De engine telt in Stap 6 puur het aantal namen in de cel (`const bezet = ingepland.length`
  // in rooster-engine.js) en trekt afwezigen er niet af; wijzigCel (useRoosterStore.js)
  // overschrijft het veld daarna met een analyseerWeek-aanroep die een LEGE afwezigheidsmap
  // meekrijgt. Een cel waarin ELKE ingeplande persoon verlof of ziekte heeft telt zo als vol
  // en levert helemaal geen conflict op.
  //
  // Dat weegt hier zwaarder dan elders, want deze functie groepeert OVER WEKEN HEEN: een slot
  // telt pas als knelpunt vanaf twee conflictweken en wordt pas 'hoog' bij drie (hieronder).
  // Eén gemiste week per slot kan het dus onder de drempel duwen en de suggestie helemaal
  // laten verdwijnen — stil, want de banner toont dezelfde melding wél.
  //
  // weekAnalyses draait analyseerWeek al over PRECIES deze weken met bouwAfwezigheidMap, dus
  // dit is hergebruik en geen tweede berekening. Zelfde bron en zelfde reden als de
  // ConflictBanner, heeftProbleem en verseConflicten in de store.
  const slotMap = {};
  weken.forEach(week => {
    const analyse = weekAnalyses[`${week.isoJaar ?? jaar}-${week.weeknummer}`];
    (analyse?.conflicten ?? []).forEach(c => {
      const key = `${c.gemeenteKey}|${c.dienst}|${c.dag}`;
      if (!slotMap[key]) slotMap[key] = { ...c, conflictWeken: [] };
      slotMap[key].conflictWeken.push(week);
    });
  });

  // Per week één keer: afwezigheidskaart, weektype en vakantiedagen. De lus hieronder is
  // slot → conflictWeken, dus dezelfde week komt voor elk slot opnieuw langs. De afwMap werd
  // daardoor al per slot herbouwd; met de vakantiedagen-Set erbij zouden dat er twee zijn.
  // `vakanties` is de LIJST met periodes, niet één Set — de Set is per week (zelfde bouw als
  // in berekenMarkeringDelta en weekAnalyses).
  const weekContext = new Map();
  const contextVoor = (week) => {
    const isoJaarWeek = week.isoJaar ?? jaar;
    const sleutel = `${isoJaarWeek}-${week.weeknummer}`;
    if (!weekContext.has(sleutel)) {
      weekContext.set(sleutel, {
        afwMap: bouwAfwezigheidMap(afwezigheid, week.weeknummer, isoJaarWeek),
        weekType: schoolweekType(week.weeknummer),
        vakDagen: new Set(vakantieDagenInWeek(week.weeknummer, isoJaarWeek, vakanties)),
      });
    }
    return weekContext.get(sleutel);
  };

  // 2. Per structureel slot: vrije medewerkers of nieuw-personeel suggestie
  Object.values(slotMap).forEach(slot => {
    if (slot.conflictWeken.length < 2) return;

    const vrijTeller = {};
    mwKeys.forEach(k => (vrijTeller[k] = 0));

    slot.conflictWeken.forEach(week => {
      const { afwMap, weekType, vakDagen } = contextVoor(week);
      mwKeys.forEach(k => {
        if (isVrijEnGeschikt(k, mwrs[k], slot.dag, slot.gemeenteKey, slot.dienst, week.rooster, week.weeknummer, afwMap, weekType, vakDagen)) {
          vrijTeller[k]++;
        }
      });
    });

    const drempel = Math.ceil(slot.conflictWeken.length / 2);
    const vrijeMw = mwKeys.filter(k => vrijTeller[k] >= drempel).map(k => mwrs[k].naam);

    const dienstKort = slot.dienst === 'dagdienst' ? 'dagdienst' : slot.dienst === 'flexibel' ? 'flexibel' : 'laat';
    const conflictWeekTypes = [...new Set(slot.conflictWeken.map(w => w.schoolweekType).filter(Boolean))];
    const weekTypeLabel = conflictWeekTypes.length === 1 ? ` (week ${conflictWeekTypes[0]})` : '';
    const slotLabel = `${slot.gemeente} ${dienstKort} ${slot.dag}${weekTypeLabel}`;
    const prio = slot.conflictWeken.length >= 3 ? 'hoog' : 'midden';

    if (vrijeMw.length > 0) {
      const top = vrijeMw.slice(0, 3);
      const namenStr = top.length === 1 ? top[0] : top.slice(0, -1).join(', ') + ' en ' + top[top.length - 1];
      suggesties.push({
        type: 'vrije_medewerker', prioriteit: prio, badge: slotLabel,
        // BEWUST GEEN kalenderdatum: dit slot komt pas in de lijst bij minstens twee weken
        // (de conflictWeken-poort hierboven), dus er is geen enkele datum die klopt. Het
        // aantal weken zegt hier wat de datum niet kan: dat het structureel is.
        tekst: `${namenStr} ${top.length === 1 ? 'heeft' : 'hebben'} ${DAG_LANG_KN[slot.dag] || slot.dag} niets — overweeg ${slotLabel} (${wekenLabel(slot.conflictWeken.length)})`,
        weken: slot.conflictWeken.length,
        gemeenteKey: slot.gemeenteKey, dag: slot.dag, dienst: slot.dienst, // sorteersleutels
      });
    } else {
      // Tekort in PERSONEN, uit het conflict zelf (slot = spread van het eerste conflict).
      // Stond eerder als koppelsNodig − conflictWeken[0].koppelsBezet: dat las een WEEK-object,
      // dat geen koppelsBezet heeft, dus het aftrekdeel was altijd 0 én de eenheid was koppels.
      //
      // TWEE FORMATEN. Sinds de personen-omzetting (28 juli) dragen conflicten bezet/nodig in
      // PERSONEN; maandroosters die vóór die datum zijn bewaard dragen koppelsBezet/koppelsNodig
      // in KOPPELS. Die oude blijven leesbaar via selecteerMaand, en zonder terugval rekende dit
      // `undefined - undefined` = NaN — en Math.max(1, NaN) is óók NaN, dus er stond letterlijk
      // "Voeg NaN medewerkers toe". Gemeten op juli 2026 in de dev-data.
      // Koppels → personen met dezelfde koppel_grootte die de engine gebruikt (analyseer-week.js:70).
      const koppelGrootte = regels.instellingen?.koppel_grootte || 2;
      const tekort = Number.isFinite(slot.nodig) && Number.isFinite(slot.bezet)
        ? slot.nodig - slot.bezet
        : (Number.isFinite(slot.koppelsNodig) && Number.isFinite(slot.koppelsBezet)
          ? (slot.koppelsNodig - slot.koppelsBezet) * koppelGrootte
          : null);
      // Geen van beide vormen aanwezig → een zin ZONDER getal. Liever geen aantal dan een
      // verzonnen 1: de teamleider zou daar anders op plannen.
      const n = tekort === null ? null : Math.max(1, tekort);
      const aantalTekst = n === null
        ? 'Voeg personeel toe'
        : `Voeg ${n === 1 ? '1 medewerker' : `${n} medewerkers`} toe`;
      suggesties.push({
        type: 'slot_tekort', prioriteit: prio, badge: slotLabel,
        // Ook hier geen datum, om dezelfde reden als hierboven.
        tekst: `${aantalTekst} met voorkeur ${slot.gemeente} ${dienstKort} — speelt in ${wekenLabel(slot.conflictWeken.length)}`,
        weken: slot.conflictWeken.length,
        gemeenteKey: slot.gemeenteKey, dag: slot.dag, dienst: slot.dienst, // sorteersleutels
      });
    }
  });

  // 3. Balans-tekorten > 16u — week-modus: één venster; maand-modus (blok-vorm): per blok,
  //    want het blok is de rekeneenheid. De afwijking komt VERS uit versBalansVoorBlok, met
  //    dezelfde invoer als BalansTabel (inclusief markeringDelta), zodat het advies en de
  //    tabel niet uit elkaar kunnen lopen.
  const balansBronnen = samenvatting
    ? [{ label: `over ${weken.length} weken`, samenvatting, blokWeken: weken, isoJaar: weken[0]?.isoJaar ?? jaar }]
    : (maandrooster.samenvattingPerBlok || []).map(b => ({
        label: `in blok ${b.blok} (week ${b.weken[0]}–${b.weken[b.weken.length - 1]})`,
        samenvatting: b.samenvatting,
        blokWeken: b.weken,
        isoJaar: b.isoJaar,
      }));
  balansBronnen.forEach(bron => {
    const blokWeekObjecten = bron.blokWeken
      .map(w => (typeof w === 'object' && w !== null
        ? w
        : zoekWeekObject(bron.isoJaar, w, maandroosters, vastgelegdeWeken)))
      .filter(Boolean);
    const markeringDelta = berekenMarkeringDelta(
      blokWeekObjecten, mwrs, celOverrides, afwezigheid, jaar, regels, vakanties,
    );
    mwKeys.forEach(k => {
      const mw = mwrs[k];
      const r = versBalansVoorBlok({
        blokWeken: bron.blokWeken, isoJaar: bron.isoJaar, mwKey: k, mw,
        maandroosters, vastgelegdeWeken, afwezigheid, regels, vakanties,
        markeringDelta: markeringDelta[mw.naam] || 0,
        bevrorenCumUren: bron.samenvatting?.[k]?.cumUren ?? 0,
      });
      if (r.afwijking <= -8) {
        suggesties.push({
          type: 'balans_tekort', prioriteit: r.afwijking < -32 ? 'hoog' : 'midden', badge: mw.naam,
          // Zelfde afronding als de balanstabel: deze tekst leest dezelfde verse afwijking,
          // en die kan sinds de handmatige verlofuren een gebroken getal zijn.
          tekst: `${mw.naam} staat op ${urenNet(r.afwijking)} ${bron.label} — vul handmatig aan of pas het contract aan`,
          weken: null,
        });
      }
    });
  });

  // Vaste, leesbare volgorde: urgentie → soort → gemeente → dag → dienst. Zonder die laatste
  // drie bleven items binnen één groep op engine-volgorde staan. balans_tekort heeft ze niet;
  // ontbrekende sleutels sorteren achteraan, nooit vooraan.
  const prioVolgorde = { hoog: 0, midden: 1 };
  const typeVolgorde = { slot_tekort: 0, vrije_medewerker: 1, balans_tekort: 2 };
  const dagIndex = (dag) => {
    const i = ALLE_DAGEN.indexOf(dag);
    return i === -1 ? ALLE_DAGEN.length : i;
  };
  const tekstVgl = (x, y) => {
    if (x === y) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return x.localeCompare(y, 'nl');
  };
  suggesties.sort((a, b) => {
    const opPrio = (prioVolgorde[a.prioriteit] ?? 9) - (prioVolgorde[b.prioriteit] ?? 9);
    if (opPrio !== 0) return opPrio;
    const opType = (typeVolgorde[a.type] ?? 9) - (typeVolgorde[b.type] ?? 9);
    if (opType !== 0) return opType;
    const opGem = tekstVgl(a.gemeenteKey, b.gemeenteKey);
    if (opGem !== 0) return opGem;
    const opDag = dagIndex(a.dag) - dagIndex(b.dag);
    if (opDag !== 0) return opDag;
    return tekstVgl(a.dienst, b.dienst);
  });

  return suggesties;
}

function KnelpuntenSectie({ maandrooster, regels, afwezigheid, jaar, bronnen }) {
  // Standaard open; dichtgeklapt blijft alleen de kopregel staan. Leeg → niets, ook geen kopregel.
  const [open, setOpen] = useState(true);
  const suggesties = analyseKnelpunten(maandrooster, regels, afwezigheid, jaar, bronnen);
  if (suggesties.length === 0) return null;

  const hoogAantal = suggesties.filter(s => s.prioriteit === 'hoog').length;

  return (
    <section>
      <h2 className="mb-2">
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-xs font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1.5 hover:text-slate-600"
        >
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          <Lightbulb className="w-3.5 h-3.5" />
          Knelpunten &amp; suggesties ({suggesties.length})
          {hoogAantal > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600 text-xs font-bold leading-none">
              {hoogAantal}
            </span>
          )}
        </button>
      </h2>
      {open && (
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {suggesties.map((s) => (
          <div key={`${s.type}|${s.badge}|${s.tekst}`} className="flex items-start gap-3 px-4 py-3">
            <div className={`shrink-0 mt-1.5 w-2 h-2 rounded-full ${s.prioriteit === 'hoog' ? 'bg-orange-400' : 'bg-slate-300'}`} />
            <div className="flex-1 min-w-0 text-sm leading-snug">
              <span className={`inline-block text-xs font-semibold px-1.5 py-0.5 rounded mr-2 leading-none ${
                s.type === 'vrije_medewerker' ? 'bg-blue-100 text-blue-700' :
                s.type === 'slot_tekort'      ? 'bg-orange-100 text-orange-700' :
                                               'bg-red-100 text-red-700'
              }`}>
                {s.badge}
              </span>
              <span className="text-slate-700">{s.tekst}</span>
              {s.weken && (
                <span className="ml-1.5 text-xs text-slate-400">{s.weken}/{maandrooster?.weken?.length || 4} weken</span>
              )}
            </div>
          </div>
        ))}
        <div className="px-4 py-2.5 text-xs text-slate-400 italic">
          Je beslist zelf wat je met deze suggesties doet.
        </div>
      </div>
      )}
    </section>
  );
}

// ── HOOFD-PAGINA ─────────────────────────────────────────────────────────────

export default function Weekrooster() {
  const {
    weeknummer, jaar,
    maandrooster, roosterStatus, conflicten, roosterLog,
    bijzonderheden, setBijzonderheid,
    genereerRooster: doGenereer,
    maandSelectie, selecteerMaand, genereerMaand, maandroosters,
    regels, afwezigheid, vakanties,
    vastgelegdeWeken, legWeekVast, ontgrendelWeek, trekWeekGelijk,
    wijzigCel, celOverrides, setCelOverride,
    // Afgevinkte meldingen uit het paneel — client-eigen en teambreed, zie de store.
    afgevinkteMeldingen, zetMeldingAfgevinkt,
    // Alleen om te LEZEN — zie de toelichting bij de initiële waarde in de store.
    ruilWissels,
    // Idem, maar dit veld heeft GEEN initiële waarde in de store: het landt pas bij de eerste
    // laadVanServer via de merge in serverSync.js:84, en is daarvóór undefined. De opzoeking
    // verdraagt dat (handmatigmarkering.js gooit op !Array.isArray), en de prop-default
    // op RoosterMatrix vangt het nog een keer af.
    wijzigingen,
  } = useRoosterStore();

  const [toonLog, setToonLog] = useState(false);
  const [actieveTab, setActieveTab] = useState(0);

  const isKlaar = roosterStatus === 'done' && maandrooster;
  const actieveWeek = isKlaar ? maandrooster.weken[actieveTab] : null;
  // Het weeknummer komt uit het week-object zelf, niet uit weeknummer + actieveTab. Doortellen
  // klopt alleen zolang de maandweken opeenvolgende gehele getallen zijn; bij een maand die twee
  // ISO-jaren mengt gaat dat mis (december 2025 was 49,50,51,52,1 — tab 5 rekende dan 53 uit
  // terwijl de week 1 is). Eerstvolgende geval: december 2029. De terugval geldt alleen als er
  // nog geen actieve week is; elk gebruik hieronder staat achter een isKlaar && actieveWeek-guard.
  const actieveWeeknummer = actieveWeek?.weeknummer ?? (weeknummer + actieveTab);

  // Type-bewuste afwezigheidskaart voor de getoonde week (display-only: V/Z/A in de matrix).
  const afwTypeWeek = (isKlaar && actieveWeek)
    ? bouwAfwezigheidTypeMap(afwezigheid, actieveWeeknummer, actieveWeek.isoJaar ?? jaar)
    : {};

  // Vakantiedagen van de getoonde week (Set<dag-code>) — zelfde bouw als in weekAnalyses
  // hieronder. Matrix en dag-weergave geven dit door aan getCel: in een schoolvakantie
  // vervalt de schooldag, dus dan geen S-cel maar een gewone, inzetbare dag.
  const vakantieDagenWeek = (isKlaar && actieveWeek)
    ? new Set(vakantieDagenInWeek(actieveWeeknummer, actieveWeek.isoJaar ?? jaar, vakanties))
    : null;

  // Eén analyse-bron voor ALLE weken (vers én bewerkt): live her-detectie tegen de HUIDIGE regels +
  // afwezigheid. Puur lezen — het rooster wordt niet aangeraakt. Gekeyd zoals isVastgelegd.
  const weekAnalyses = useMemo(() => {
    const out = {};
    for (const week of maandrooster?.weken ?? []) {
      const key = `${week.isoJaar ?? jaar}-${week.weeknummer}`;
      const vakantieDagen = new Set(vakantieDagenInWeek(week.weeknummer, week.isoJaar ?? jaar, vakanties));
      const analyse = analyseerWeek(
        week.rooster, regels, week.weeknummer,
        week.isoJaar ?? jaar, week.schoolweekType,
        { afwezigheid: bouwAfwezigheidMap(afwezigheid, week.weeknummer, week.isoJaar ?? jaar), weekCaps: week.weekCaps, vakantieDagen },
      );
      // Bekende afwezigheid (verlof/ziekte/anders) is geen planningsfout. Filtert puur de
      // OUTPUT van de engine — analyseerWeek zelf draait onveranderd. Werkt door in zowel de
      // ConflictBanner-zin als het waarschuwingsteken op de week-tab (heeftProbleem).
      const afwTypeMapWeek = bouwAfwezigheidTypeMap(afwezigheid, week.weeknummer, week.isoJaar ?? jaar);
      out[key] = {
        ...analyse,
        gemeenteSignalen: (analyse.gemeenteSignalen ?? []).filter(
          (s) => !(afwTypeMapWeek[s.medewerker] && afwTypeMapWeek[s.medewerker][s.dag]),
        ),
      };
    }
    return out;
  }, [maandrooster?.weken, regels, afwezigheid, jaar, vakanties]);

  // Geaggregeerde signalen voor de banner — uit weekAnalyses (alle weken, ook vers gegenereerd).
  //
  // Object.ENTRIES, niet values: de sleutel is `${isoJaar}-${weeknummer}` (weekAnalyses) en is de
  // ENIGE plek waar de week van deze signalen staat. De signaal-objecten zelf dragen hem niet
  // (analyseer-week.js:164 rust, :179 dubbel, :231 gemeente), dus met Object.values kwamen ze
  // zonder week-context bij de banner aan en kon die alleen een dagnaam tonen. Hier krijgen ze
  // hun kalenderdatum mee — in de renderlaag, net zoals de reeks-melding dat verderop doet
  // (reeksSignalen, vanDatum/totDatum). De engine en de signaal-vorm blijven ongemoeid.
  const signalenMetDatum = (veld, dagVeld) => (isKlaar
    ? Object.entries(weekAnalyses).flatMap(([sleutel, analyse]) => {
        const [isoJaarDeel, weekDeel] = sleutel.split('-');
        const isoJaarWeek = Number(isoJaarDeel);
        const weeknummerWeek = Number(weekDeel);
        return (analyse[veld] ?? []).map(s => ({
          ...s,
          datum: meldingDatum(weeknummerWeek, s[dagVeld], isoJaarWeek),
          // De week ook als GETALLEN, náást de geformatteerde `datum`. Die laatste is een
          // string die null kan zijn (meldingDatum, r.46-50: onbekende dagcode of een
          // niet-eindig week/jaar). De banner groepeert meldingen op hun cel-adres, en een
          // groepeersleutel die null kan worden gooit meldingen uit VERSCHILLENDE weken op
          // één hoop. weeknummer/isoJaar zijn daarvoor de betrouwbare sleutel; `datum` blijft
          // wat het was: puur om te tonen. De engine en de signaal-vorm blijven ongemoeid —
          // de signalen zelf dragen deze twee velden niet (analyseer-week.js:164/179/231),
          // dus overschrijven kan niet.
          weeknummer: weeknummerWeek,
          isoJaar: isoJaarWeek,
        }));
      })
    : []);

  const rustSignalen = signalenMetDatum('rustSignalen', 'laatDag');
  const dubbeleSignalen = signalenMetDatum('dubbeleSignalen', 'dag');
  const gemeenteSignalen = signalenMetDatum('gemeenteSignalen', 'dag');

  // Conflicten komen uit de store en dragen `weeknummer` (analyseer-week.js:125) maar geen
  // isoJaar. Binnen één getoonde maand komt elk weeknummer precies één keer voor — een maand
  // bestaat uit de ISO-weken waarvan de MAANDAG erin valt (data/maand.js:4) — dus dit tabelletje
  // is eenduidig. Staat de week er niet in, dan blijft isoJaar undefined en geeft meldingDatum
  // null: de melding valt dan terug op de oude tekst zonder datum.
  const isoJaarVanWeek = useMemo(() => {
    const uit = {};
    for (const w of maandrooster?.weken ?? []) uit[w.weeknummer] = w.isoJaar ?? jaar;
    return uit;
  }, [maandrooster?.weken, jaar]);

  const conflictenMetDatum = useMemo(
    () => {
      // Het TYPE per afwezige naam. Het conflict draagt alleen de NAMEN (analyse-week zet
      // `afwezigen` als namen; de engine kent het type niet, want bouwAfwezigheidMap bewaart
      // per naam alleen de dagen). Het type hoort dus hier bijgezet te worden, in dezelfde
      // renderlaag die ook `datum` en `isoJaar` bijzet.
      // Per WEEKNUMMER één keer gebouwd: dezelfde week komt voor elk conflict opnieuw langs.
      const typeMaps = {};
      const typeMapVoor = (weeknummer) => {
        if (!(weeknummer in typeMaps)) {
          typeMaps[weeknummer] = bouwAfwezigheidTypeMap(afwezigheid, weeknummer, isoJaarVanWeek[weeknummer] ?? jaar);
        }
        return typeMaps[weeknummer];
      };
      return (conflicten ?? []).map(c => ({
        ...c,
        datum: meldingDatum(c.weeknummer, c.dag, isoJaarVanWeek[c.weeknummer]),
        // isoJaar er ook als GETAL bij, uit hetzelfde tabelletje. Het conflict-object draagt hem
        // niet (zie de comment hierboven) en de afvink-sleutel heeft hem nodig: zonder jaar zou
        // week 1 van twee jaren dezelfde sleutel krijgen. Ontbreekt hij, dan blijft de sleutel
        // leeg en is de melding simpelweg niet afvinkbaar — nooit op de verkeerde cel.
        isoJaar: isoJaarVanWeek[c.weeknummer],
        // `afwezigen` van namen naar { naam, type }. Voorwaardelijk gespreid: draagt het conflict
        // het veld niet (een conflict uit een oudere opslag, of een cel zonder afwezigen), dan
        // komt het er ook niet bij en gedraagt de balk zich precies als voorheen.
        // Onbekend type → 'anders', dezelfde terugval als bouwAfwezigheidTypeMap zelf
        // (data/afwezigheid.js:148).
        ...(c.afwezigen?.length
          ? {
            afwezigen: c.afwezigen.map((naam) => ({
              naam,
              type: typeMapVoor(c.weeknummer)[naam]?.[c.dag] ?? 'anders',
            })),
          }
          : {}),
      }));
    },
    [conflicten, isoJaarVanWeek, afwezigheid, jaar],
  );

  // ── Reeks-signalen: meer dan de toegestane aaneengesloten gewerkte dagen ────────────
  // Op VENSTER-niveau en niet per week, want een reeks loopt over weekgrenzen en analyseerWeek
  // ziet er maar één. Hangt aan dezelfde bronnen als weekAnalyses, dus de melding beweegt mee
  // met zowel een handmatige celwijziging (maandrooster.weken krijgt een nieuwe identiteit) als
  // een verlofwijziging (afwezigheid verandert) — zonder dat er opnieuw gegenereerd hoeft.

  // De week direct VÓÓR het venster. Zonder die week telt een reeks die over de vensterrand
  // loopt te kort; juli/augustus 2026 is zo'n geval (wk31 → wk32 staat in twee vensters).
  // Twee bronnen, in volgorde van betrouwbaarheid: de vastgelegde kopie, anders de bewaarde
  // generatie van de maand waarin die maandag valt (Regel B). Is er geen van beide, dan blijft
  // hij null en begint de teller blanco — dezelfde blinde start die de blok-generator logt
  // (blok-generator.js:101-107).
  const vorigeVensterWeek = useMemo(() => {
    const eerste = maandrooster?.weken?.[0];
    if (!eerste) return null;
    const maandagVorige = weekNaarMaandag(eerste.weeknummer, eerste.isoJaar ?? jaar);
    maandagVorige.setDate(maandagVorige.getDate() - 7);
    const { week: vorigeWeek, isoJaar: vorigIsoJaar } = isoWeekNummer(maandagVorige);

    const vast = vastgelegdeWeken[`${vorigIsoJaar}-${vorigeWeek}`]?.week;
    if (vast?.rooster) return { ...vast, weeknummer: vorigeWeek, isoJaar: vorigIsoJaar };

    const bewaard = maandroosters[`${maandagVorige.getFullYear()}-${maandagVorige.getMonth() + 1}`];
    const uitMaand = bewaard?.weken?.find(
      (w) => w.weeknummer === vorigeWeek && (w.isoJaar ?? jaar) === vorigIsoJaar,
    );
    return uitMaand?.rooster ? uitMaand : null;
  }, [maandrooster?.weken, vastgelegdeWeken, maandroosters, jaar]);

  const reeksSignalen = useMemo(() => {
    // Eerst binden, dan gebruiken: de React-compiler leidt anders `maandrooster` als dependency
    // af i.p.v. `maandrooster.weken` en kan de handmatige memoization niet bewaren.
    const vensterWeken = maandrooster?.weken ?? [];
    if (!vensterWeken.length) return [];
    const rij = [vorigeVensterWeek, ...vensterWeken].filter(Boolean);
    const afwMaps = {};
    const vakantieMaps = {};
    const isoVanWeek = {};
    for (const w of rij) {
      const isoJaarWeek = w.isoJaar ?? jaar;
      afwMaps[w.weeknummer] = bouwAfwezigheidMap(afwezigheid, w.weeknummer, isoJaarWeek);
      vakantieMaps[w.weeknummer] = new Set(vakantieDagenInWeek(w.weeknummer, isoJaarWeek, vakanties));
      isoVanWeek[w.weeknummer] = isoJaarWeek;
    }
    return bepaalReeksSignalen(rij, regels, { afwMaps, vakantieMaps }).map((s) => {
      // Welke week-tabs raakt deze reeks? De weken liggen aaneengesloten in `rij`, dus de strook
      // tussen begin en eind is precies het bereik. Voedt het tab-teken in heeftProbleem.
      const van = rij.findIndex((w) => w.weeknummer === s.vanWeek);
      const tot = rij.findIndex((w) => w.weeknummer === s.totWeek);
      const weekSleutels = van === -1 || tot === -1
        ? []
        : rij.slice(van, tot + 1).map((w) => `${w.isoJaar ?? jaar}-${w.weeknummer}`);
      return {
        ...s,
        vanDatum: reeksDatum(s.vanWeek, s.vanDag, isoVanWeek[s.vanWeek] ?? jaar),
        totDatum: reeksDatum(s.totWeek, s.totDag, isoVanWeek[s.totWeek] ?? jaar),
        // Het ISO-jaar van de BEGINWEEK als getal — de afvink-sleutel gebruikt (mwKey,
        // isoJaar, vanWeek, vanDag). Het zat al in isoVanWeek maar ging alleen als
        // geformatteerde datum verder; een sleutel op een datumstring zou breken zodra de
        // opmaak wijzigt.
        isoJaar: isoVanWeek[s.vanWeek] ?? jaar,
        weekSleutels,
      };
    });
  }, [maandrooster?.weken, vorigeVensterWeek, regels, afwezigheid, vakanties, jaar]);

  // Weken waarvan de tab een waarschuwingsteken moet krijgen omdat er een reeks doorheen loopt.
  const reeksWeekSleutels = useMemo(
    () => new Set(reeksSignalen.flatMap((s) => s.weekSleutels)),
    [reeksSignalen],
  );

  // Maand-kiezer: de actieve selectie, of anders de maand van de huidige ISO-week
  const maandagHuidig = weekNaarMaandag(weeknummer, jaar);
  const kiezerJaar  = maandSelectie ? maandSelectie.jaar  : maandagHuidig.getFullYear();
  const kiezerMaand = maandSelectie ? maandSelectie.maand : maandagHuidig.getMonth() + 1;
  const maandInfo = maandSelectie ? maandWeken(maandSelectie.jaar, maandSelectie.maand) : null;
  const laatsteWeekIdx = maandSelectie ? maandInfo.aantal - 1 : 3;
  const vorigeMaand   = () => selecteerMaand(kiezerMaand === 1 ? kiezerJaar - 1 : kiezerJaar, kiezerMaand === 1 ? 12 : kiezerMaand - 1);
  const volgendeMaand = () => selecteerMaand(kiezerMaand === 12 ? kiezerJaar + 1 : kiezerJaar, kiezerMaand === 12 ? 1 : kiezerMaand + 1);

  // Mount-restore: de kiezer toont bij openen al de juiste maand, maar de live weergave
  // (maandrooster) wordt niet gepersisteerd — alleen maandroosters[jaar-maand]. Is er nog
  // geen live rooster én bestaat er een bewaarde generatie voor de getoonde maand, herstel
  // die dan via de bestaande selecteerMaand-actie. Guard op leeg maandrooster zodat een
  // handmatige selectie nooit overschreven wordt. Sleutel exact zoals de store (jaar-maand).
  useEffect(() => {
    if (maandrooster) return;
    if (maandroosters[`${kiezerJaar}-${kiezerMaand}`]) selecteerMaand(kiezerJaar, kiezerMaand);
  }, [kiezerJaar, kiezerMaand, maandroosters, maandrooster, selecteerMaand]);

  // Reset tab als we een nieuw rooster genereren
  const handleGenereer = () => {
    setActieveTab(0);
    if (maandSelectie) genereerMaand(maandSelectie.jaar, maandSelectie.maand);
    else doGenereer();
  };

  // ── Vastleggen ───────────────────────────────────────────────────────────
  // Een week is vastgelegd als zijn sleutel `${isoJaar}-${weeknummer}` in de store
  // staat. Zelfde sleutel-afleiding als legWeekVast: isoJaar valt terug op jaar.
  const isVastgelegd = (week) =>
    !!week && !!vastgelegdeWeken[`${week.isoJaar ?? jaar}-${week.weeknummer}`];

  // Ziet het team voor de ACTIEVE week iets anders dan deze matrix? De vastgelegde
  // kopie is wat de server aan TeamRooster en Mijn Rooster serveert
  // (momentopnameRoosterVoorWeek in rooster-service.js);
  // de matrix leest maandrooster. Lopen die uiteen, dan volgt het team een ander rooster
  // dan de teamleider hier ziet — het voorval van 2026-08-05 (10 cellen, §36).
  // Pure volgordeverschillen tellen NIET mee: zie echteAfwijkingen in lib/divergentie.js.
  const afwijkingenActieveWeek = useMemo(() => {
    if (!actieveWeek) return [];
    const wKey = `${actieveWeek.isoJaar ?? jaar}-${actieveWeek.weeknummer}`;
    if (!vastgelegdeWeken[wKey]) return [];
    return echteAfwijkingen({
      vastgelegdeWeken: { [wKey]: vastgelegdeWeken[wKey] },
      maandroosters,
    }).filter(
      (c) => c.weeknummer === actieveWeek.weeknummer
        && (c.isoJaar ?? null) === (actieveWeek.isoJaar ?? jaar ?? null),
    );
  }, [actieveWeek, vastgelegdeWeken, maandroosters, jaar]);

  // Overschrijft de vastgelegde kopie met de matrix. Bevestiging met het aantal en de
  // week erin: dit wijzigt wat het hele team ziet, dus geen kale klik.
  const handleTrekGelijk = () => {
    if (!actieveWeek || afwijkingenActieveWeek.length === 0) return;
    const n = afwijkingenActieveWeek.length;
    const ok = window.confirm(
      `Het rooster van het team voor week ${actieveWeek.weeknummer} wordt gelijkgemaakt aan wat je hier ziet.\n\n` +
      `${n} ${n === 1 ? 'dienst wordt' : 'diensten worden'} bijgewerkt. Dit kan niet ongedaan worden gemaakt.\n\n` +
      `Doorgaan?`,
    );
    if (ok) trekWeekGelijk(actieveWeek, n);
  };

  // Heeft deze week een probleem (onderbezetting/rust/dubbel/gemeente/reeks)? Voor het tab-teken.
  // Twee bronnen: weekAnalyses (alle weken, vers én vergrendeld) — som van de 5 velden > 0 — en
  // de reeks-signalen, die venster-breed berekend zijn en elke week markeren die ze doorkruisen.
  const heeftProbleem = (week) => {
    const sleutel = `${week.isoJaar ?? jaar}-${week.weeknummer}`;
    if (reeksWeekSleutels.has(sleutel)) return true;
    const a = weekAnalyses[sleutel];
    if (!a) return false;
    return (a.conflicten?.length || 0)
      + (a.caoOvertredingen?.length || 0)
      + (a.rustSignalen?.length || 0)
      + (a.dubbeleSignalen?.length || 0)
      + (a.gemeenteSignalen?.length || 0) > 0;
  };

  const alleVastgelegd = isKlaar && maandrooster.weken.every(isVastgelegd);
  // De melding "verlof/ziekte gewijzigd sinds deze generatie" is hier weggehaald (samen met
  // de vingerafdruk-helper die hem voedde). Hij dubbelde: het verlof staat al als V/Z in de
  // matrix, en het enige gevolg dat er écht toe doet — een dienst die onbemand raakt — wordt
  // specifieker gemeld door de onderbezetting in Knelpunten. Wat overbleef was ruis die de
  // teamleider aanzette tot een hergeneratie die niets oploste.
  // `gegenereerdMetAfwezigheid` wordt in de store nog wél geschreven; zie de comment daar.

  const handleHeleMaandVastleggen = () => {
    if (!isKlaar) return;
    maandrooster.weken.forEach(week => { if (!isVastgelegd(week)) legWeekVast(week); });
  };
  const handleHeleMaandOntgrendelen = () => {
    if (!isKlaar) return;
    const ok = window.confirm('Let op: dit rooster is mogelijk al gepubliceerd naar het team. Weet je zeker dat je de hele maand wilt ontgrendelen?');
    if (ok) maandrooster.weken.forEach(week => { if (isVastgelegd(week)) ontgrendelWeek(week.isoJaar ?? jaar, week.weeknummer); });
  };

  // Publicatiemelding: bewust een aparte handeling, want vastleggen-en-meteen-ontgrendelen
  // (om iets te testen) mag geen bericht naar het team sturen. De echte dubbelverzend-grendel
  // staat server-side; `meldingBezig` vangt alleen de dubbelklik.
  const [meldingBezig, setMeldingBezig] = useState(false);
  const handleStuurPublicatieMelding = async () => {
    if (!isKlaar || !maandSelectie || meldingBezig) return;
    const { jaar: mJaar, maand: mMaand } = maandSelectie;
    const label = `${MAANDEN[mMaand - 1]} ${mJaar}`;
    if (!window.confirm(`Het hele team krijgt een melding dat het rooster voor ${label} klaarstaat. Doorgaan?`)) return;

    const verstuur = async (nogmaals) => {
      const res = await stuurPublicatieMelding(mJaar, mMaand, nogmaals);
      const n = res.aantal;
      window.alert(`Melding verstuurd naar ${n} ${n === 1 ? 'medewerker' : 'medewerkers'}.`);
    };

    setMeldingBezig(true);
    try {
      await verstuur(false);
    } catch (err) {
      // 409 + herhalenMogelijk = deze maand is al eens aangekondigd; de servertekst noemt
      // de eerdere datum. Alleen na een expliciete bevestiging nogmaals sturen.
      const kanHerhalen = err.status === 409 && err.body?.herhalenMogelijk;
      if (!kanHerhalen) {
        window.alert(err.message);
      } else if (window.confirm(`${err.message}\n\nToch nogmaals sturen?`)) {
        try { await verstuur(true); } catch (err2) { window.alert(err2.message); }
      }
    } finally {
      setMeldingBezig(false);
    }
  };

  const handleExport = async () => {
    const label = `${MAANDEN[maandSelectie.maand - 1]} ${maandSelectie.jaar}`;
    const ok = await exporteerMaandroosterExcel(maandrooster, regels, afwezigheid, label, celOverrides, bijzonderheden, vakanties);
    if (!ok) window.alert('Exporteren naar Excel is mislukt. Bekijk het logboek voor details.');
  };

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            {maandSelectie
              ? `Maandrooster — ${MAANDEN[maandSelectie.maand - 1]} ${maandSelectie.jaar}`
              : '4-Weken Rooster'}
          </h1>
        </div>
        {/* Onder lg mag deze balk wrappen; de knopmarges gelden pas vanaf lg, anders springt
            een knop die als eerste op een nieuwe regel belandt scheef in. */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button onClick={vorigeMaand} className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => selecteerMaand(kiezerJaar, kiezerMaand)}
            title="Plan deze hele maand (Regel B: de maandag bepaalt de maand)"
            className={`px-3 py-2 rounded-lg border text-sm font-semibold min-w-[150px] text-center ${
              maandSelectie ? 'bg-brand-50 border-brand-300 text-brand-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {MAANDEN[kiezerMaand - 1]} {kiezerJaar}
          </button>
          <button onClick={volgendeMaand} className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={handleGenereer}
            disabled={alleVastgelegd || roosterStatus === 'loading'}
            title={alleVastgelegd ? 'Ontgrendel de maand eerst om opnieuw te genereren' : undefined}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition lg:ml-2 ${
              alleVastgelegd
                ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                : 'bg-brand-700 text-white hover:bg-brand-800 disabled:opacity-50'
            }`}
          >
            <RefreshCw className={`w-4 h-4 ${roosterStatus === 'loading' ? 'animate-spin' : ''}`} />
            {maandSelectie ? `Genereer maandrooster (${maandInfo.aantal} weken)` : 'Genereer 4-weken rooster'}
          </button>
          {isKlaar && maandSelectie && (
            <button
              onClick={handleExport}
              title="Exporteer dit maandrooster naar Excel"
              className="flex items-center gap-2 px-4 py-2 bg-brand-900 text-white rounded-lg text-sm font-semibold hover:bg-brand-950 transition lg:ml-1"
            >
              <Download className="w-4 h-4" />
              Exporteer naar Excel
            </button>
          )}
        </div>
      </div>

      {/* Genereren geblokkeerd zolang de maand vastligt — zichtbare uitleg i.p.v. dode klik */}
      {alleVastgelegd && (
        <p className="-mt-3 text-right text-xs text-slate-500">
          Deze maand is vastgelegd. Ontgrendel eerst om opnieuw te genereren.
        </p>
      )}

      {/* Lege toestand */}
      {roosterStatus === 'idle' && (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">
          <div className="text-5xl mb-4">📅</div>
          <p className="text-lg font-medium text-slate-500">Nog geen rooster gegenereerd.</p>
          <p className="text-sm mt-1">
            Klik op <strong>{maandSelectie ? 'Genereer maandrooster' : 'Genereer 4-weken rooster'}</strong> om weken {weeknummer}–{weeknummer + laatsteWeekIdx} in te plannen.
          </p>
        </div>
      )}

      {/* Generatie mislukt — zonder deze tak blijft er alleen een lege pagina met de Legenda over,
          en is zelfs de opgeslagen foutmelding onzichtbaar (de engine-log is op 'done' gated). */}
      {roosterStatus === 'error' && (
        <div className="bg-red-50 rounded-2xl border border-red-200 p-12 text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <p className="text-lg font-medium text-red-800">Er ging iets mis bij het genereren.</p>
          <p className="text-sm mt-1 text-red-700">
            Herlaad de pagina en probeer het opnieuw. Blijft het misgaan, geef dan onderstaande melding door.
          </p>
          {roosterLog?.[0] && (
            <p className="mt-4 text-xs font-mono text-red-600 break-words">{roosterLog[0]}</p>
          )}
        </div>
      )}

      {/* Meldingen (alle weken gecombineerd): rode en gele sectie, elk apart inklapbaar.
          caoOvertredingen wordt hier bewust NIET meegegeven: controleerCAOGrenzen is
          uitgeschakeld (rooster-engine.js) en geeft altijd [] — de prop werd al genegeerd. */}
      {isKlaar && (
        <ConflictBanner
          conflicten={conflictenMetDatum}
          rustSignalen={rustSignalen}
          dubbeleSignalen={dubbeleSignalen}
          gemeenteSignalen={gemeenteSignalen}
          reeksSignalen={reeksSignalen}
          afgevinkt={afgevinkteMeldingen}
          onVink={(sleutel, peil) => zetMeldingAfgevinkt({ sleutel, peil })}
        />
      )}

      {/* Knelpunten & suggesties */}
      {isKlaar && (
        <KnelpuntenSectie
          maandrooster={maandrooster}
          regels={regels}
          afwezigheid={afwezigheid}
          jaar={jaar}
          bronnen={{ maandroosters, vastgelegdeWeken, vakanties, celOverrides, weekAnalyses }}
        />
      )}

      {/* Vastleggen-acties */}
      {isKlaar && actieveWeek && (
        <div className="flex flex-wrap items-center gap-2">
          {alleVastgelegd ? (
            <>
              <button
                onClick={handleHeleMaandOntgrendelen}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition"
              >
                <Unlock className="w-4 h-4" />
                Hele maand ontgrendelen
              </button>
              {/* Alleen in maand-modus: het endpoint werkt per jaar+maand, en in de
                  4-weken-modus is maandSelectie null (zie ook de Excel-knop). */}
              {maandSelectie && (
                <button
                  onClick={handleStuurPublicatieMelding}
                  disabled={meldingBezig}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                  {meldingBezig ? 'Bezig met versturen…' : 'Stuur melding naar het team'}
                </button>
              )}
            </>
          ) : (
            <button
              onClick={handleHeleMaandVastleggen}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition"
            >
              <Lock className="w-4 h-4" />
              Hele maand vastleggen
            </button>
          )}
        </div>
      )}

      {/* Week-tabs */}
      {isKlaar && (
        <div className="flex flex-wrap gap-1 bg-slate-100 rounded-xl p-1 w-fit max-w-full">
          {maandrooster.weken.map((week, i) => {
            const vast = isVastgelegd(week);
            const probleem = heeftProbleem(week);
            return (
              <button
                key={i}
                onClick={() => setActieveTab(i)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition whitespace-nowrap shrink-0 ${
                  actieveTab === i
                    ? (vast ? 'bg-emerald-100 text-emerald-900 shadow-sm ring-1 ring-emerald-300' : 'bg-white text-slate-800 shadow-sm')
                    : (vast ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'text-slate-500 hover:text-slate-700')
                }`}
              >
                <div className="flex items-center gap-1">
                  {vast && <Lock className="w-3 h-3 shrink-0" />}
                  {probleem && <AlertTriangle className="w-3 h-3 shrink-0 text-amber-500" />}
                  <span>Week {week.weeknummer}{week.blok ? ` · blok ${week.blok}` : ''}</span>
                </div>
                <div className="text-xs font-normal opacity-60">{weekBereikKort(week.weeknummer, week.isoJaar ?? jaar)}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Divergentie-waarschuwing: het team ziet iets anders dan deze matrix */}
      {isKlaar && actieveWeek && afwijkingenActieveWeek.length > 0 && (
        <div className="rounded-xl border border-red-300 bg-red-50 p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 shrink-0 text-red-600 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-red-900">
                Let op: de medewerkers zien niet wat jij hier ziet.
              </p>
              <p className="text-sm text-red-800 mt-1">
                {afwijkingenActieveWeek.length === 1
                  ? '1 dienst in deze week staat bij hen anders.'
                  : `${afwijkingenActieveWeek.length} diensten in deze week staan bij hen anders.`}
              </p>
              <details className="mt-2">
                <summary className="text-xs font-semibold text-red-700 cursor-pointer">
                  Toon welke
                </summary>
                <ul className="mt-2 space-y-1">
                  {afwijkingenActieveWeek.map((c, i) => (
                    <li key={i} className="text-xs text-red-800">
                      <span className="font-semibold">{DAG_LANG[c.dag] || c.dag}</span>
                      {' · '}
                      {regels.gemeenten?.[c.gemeenteKey]?.naam || c.gemeenteKey}
                      {c.alleenMatrix.length > 0 && (
                        <span className="block ml-3">
                          staat hier wel, bij het team niet: {c.alleenMatrix.join(', ')}
                        </span>
                      )}
                      {c.alleenVast.length > 0 && (
                        <span className="block ml-3">
                          ziet het team wel, hier niet: {c.alleenVast.join(', ')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </details>
              <button
                onClick={handleTrekGelijk}
                className="mt-3 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-red-300 bg-white text-red-700 hover:bg-red-100 transition"
              >
                <RefreshCw className="w-4 h-4" />
                Team bijwerken
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 1. MATRIX */}
      {isKlaar && actieveWeek && (
        <section>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
            Matrix — week {actieveWeeknummer}
          </h2>
          <RoosterMatrix
            jaar={jaar}
            rooster={actieveWeek.rooster}
            medewerkers={regels.medewerkers}
            weeknummer={actieveWeeknummer}
            bijzonderheden={bijzonderheden}
            setBijzonderheid={setBijzonderheid}
            weekIndex={actieveTab}
            regels={regels}
            wijzigCel={wijzigCel}
            afwTypeWeek={afwTypeWeek}
            celOverrides={celOverrides}
            setCelOverride={setCelOverride}
            vakantieDagen={vakantieDagenWeek}
            ruilWissels={ruilWissels}
            wijzigingen={wijzigingen}
            isoJaar={actieveWeek.isoJaar ?? jaar}
          />
        </section>
      )}

      {/* 2. DAG-WEERGAVE */}
      {isKlaar && actieveWeek && (
        <section>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Dag-weergave</h2>
          <DagWeergave
            rooster={actieveWeek.rooster}
            medewerkers={regels.medewerkers}
            weeknummer={actieveWeeknummer}
            jaar={jaar}
            afwTypeWeek={afwTypeWeek}
            celOverrides={celOverrides}
            regels={regels}
            vakantieDagen={vakantieDagenWeek}
          />
        </section>
      )}

      {/* 3. UREN BALANS — maand-modus: per blok (rekeneenheid); week-modus: venster */}
      {isKlaar && (
        <section className="space-y-4">
          {maandrooster.samenvattingPerBlok
            ? maandrooster.samenvattingPerBlok.map(b => {
                // ALLE weken van het blok, niet alleen die in het maandvenster: het blok is de
                // rekeneenheid en loopt over de maandgrens (blok 8 = week 29-32, waarvan 29-31
                // in maandrooster 2026-7 staan). Eerder filterde dit op maandrooster.weken en
                // telde de markering-correctie dus maar één van de vier weken mee.
                const blokWeekObjecten = b.weken
                  .map(wk => zoekWeekObject(b.isoJaar, wk, maandroosters, vastgelegdeWeken))
                  .filter(Boolean);
                const markeringDelta = berekenMarkeringDelta(blokWeekObjecten, regels.medewerkers, celOverrides, afwezigheid, jaar, regels, vakanties);
                return (
                  <BalansTabel
                    key={`${b.isoJaar}-${b.blok}`}
                    samenvatting={b.samenvatting}
                    aantalWeken={b.weken.length}
                    titel={`Uren Balans — Blok ${b.blok} (week ${b.weken[0]}–${b.weken[b.weken.length - 1]}, ${b.weken.length} weken)`}
                    markeringDelta={markeringDelta}
                    blokWeken={b.weken}
                    isoJaar={b.isoJaar}
                    maandroosters={maandroosters}
                    vastgelegdeWeken={vastgelegdeWeken}
                    afwezigheid={afwezigheid}
                    regels={regels}
                    vakanties={vakanties}
                  />
                );
              })
            : (
              // Week-modus: geen blokken, dus de vensterweken ZELF zijn het rekenvenster.
              // Die dragen hun eigen isoJaar, wat versBalansVoorBlok per week overneemt.
              <BalansTabel
                samenvatting={maandrooster.samenvatting}
                aantalWeken={maandrooster.weken.length}
                blokWeken={maandrooster.weken}
                isoJaar={maandrooster.weken[0]?.isoJaar ?? jaar}
                maandroosters={maandroosters}
                vastgelegdeWeken={vastgelegdeWeken}
                afwezigheid={afwezigheid}
                regels={regels}
                vakanties={vakanties}
              />
            )}
        </section>
      )}

      {/* 4. LEGENDA */}
      <Legenda regels={regels} />

      {/* Engine-log */}
      {roosterStatus === 'done' && (
        <div>
          <button
            onClick={() => setToonLog(!toonLog)}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600"
          >
            <Info className="w-3 h-3" />
            {toonLog ? 'Verberg' : 'Toon'} engine-log ({roosterLog.length} regels)
          </button>
          {toonLog && (
            <div className="mt-2 bg-slate-900 text-slate-200 rounded-xl p-4 text-xs font-mono max-h-64 overflow-y-auto space-y-0.5">
              {roosterLog.map((r, i) => (
                <div
                  key={i}
                  className={
                    r.startsWith('[WARN]') || r.startsWith('[CAO]') ? 'text-orange-400' :
                    r.startsWith('[BUDGET]') ? 'text-emerald-400' :
                    r.startsWith('===') ? 'text-blue-400 font-bold mt-2' :
                    r.startsWith('[SKIP]') ? 'text-slate-500' : ''
                  }
                >
                  {r}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
